//© 2025 Kaitlyn Troxell
import React, { useState, useEffect } from 'react';
import SidingSelector from './SidingSelector';
import PaintColorSelector from './PaintColorSelector';
import SinkSelector from './SinkSelector';
import CabinetSelector from './CabinetSelector';
import KitchenFaucetSelector from './KitchenFaucetSelector';
import '../styles/FormStyles.css';
import {openPrintPreview} from './SelectionsPrintView.js';
import PageHeader from './PageHeader';

// Reusable Components
function FormField({ label, value, onChange, disabled, textarea, rows = 3, placeholder, type = "text", required = false, error = "", helper = "" }) {
  return (
    <div className="form-field">
      <label className="form-label">
        {label}
        {required && <span className="form-required">*</span>}
      </label>
      {textarea ? (
        <textarea
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          rows={rows}
          placeholder={placeholder}
          className={`form-textarea ${error ? 'error' : ''}`}
        />
      ) : (
        <input
          type={type}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          placeholder={placeholder}
          className={`form-input ${error ? 'error' : ''}`}
        />
      )}
      {error && <div className="form-error-message">{error}</div>}
      {helper && <div className="form-helper-text">{helper}</div>}
    </div>
  );
}

function SelectField({ label, value, onChange, disabled, options, placeholder = "-- Select an option --", required = false, error = "", helper = "" }) {
  return (
    <div className="form-field">
      <label className="form-label">
        {label}
        {required && <span className="form-required">*</span>}
      </label>
      <select
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className={`form-select ${error ? 'error' : ''}`}
      >
        <option value="">{placeholder}</option>
        {options.map((opt) => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>
      {error && <div className="form-error-message">{error}</div>}
      {helper && <div className="form-helper-text">{helper}</div>}
    </div>
  );
}

function CheckboxField({ label, checked, onChange, disabled }) {
  return (
    <div className="form-checkbox-container">
      <input
        type="checkbox"
        checked={checked || false}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
        className="form-checkbox"
        id={`checkbox-${label.replace(/\s+/g, '-')}`}
      />
      <label 
        htmlFor={`checkbox-${label.replace(/\s+/g, '-')}`}
        className="form-checkbox-label"
      >
        {label}
      </label>
    </div>
  );
}

function MultiCheckbox({ label, options, values = [], onChange, disabled, required = false }) {
  const handleToggle = (option) => {
    if (disabled) return;
    const newValues = values.includes(option)
      ? values.filter(v => v !== option)
      : [...values, option];
    onChange(newValues);
  };

  return (
    <div className="form-multi-checkbox-container">
      <label className="form-label">
        {label}
        {required && <span className="form-required">*</span>}
      </label>
      <div className="form-multi-checkbox-options">
        {options.map((option) => (
          <div key={option} className="form-multi-checkbox-option">
            <input
              type="checkbox"
              checked={values.includes(option)}
              onChange={() => handleToggle(option)}
              disabled={disabled}
              id={`multi-${label.replace(/\s+/g, '-')}-${option.replace(/\s+/g, '-')}`}
            />
            <label htmlFor={`multi-${label.replace(/\s+/g, '-')}-${option.replace(/\s+/g, '-')}`}>
              {option}
            </label>
          </div>
        ))}
      </div>
    </div>
  );
}

function TrimSelector({ label, value, onChange, disabled }) {
  const [showOptions, setShowOptions] = useState(false);

  const baseOptions = [
    { name: 'L635A', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/baseboards/L635A.JPG' },
    { name: 'L724B', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/baseboards/L724B.JPG' },
    { name: '87636', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/baseboards/87636.JPG' },
    { name: '0L634', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/baseboards/0L634.JPG' },
    { name: '0L724', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/baseboards/0L724.JPG' },
    { name: '07634', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/baseboards/07634.JPG' },
    { name: 'LA433', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/baseboards/LA433.JPG' },
    { name: '07623', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/baseboards/07623.JPG' },
    { name: '0W623', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/baseboards/0W623.JPG' },
    { name: '0W713', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/baseboards/0W713.JPG' },
    { name: '72135', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/baseboards/72135.JPG' },
    { name: '05093', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/baseboards/05093.JPG' },
    { name: '87637', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/baseboards/87637.JPG' },
    { name: '0L723', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/baseboards/0L723.JPG' },
    { name: '03102', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/baseboards/03102.JPG' },
    { name: '0L633', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/baseboards/0L633.JPG' },
    { name: 'L633A', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/baseboards/L633A.JPG' },
    { name: '07113', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/baseboards/07113.JPG' },
    { name: '0W663', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/baseboards/0W663.JPG' },
    { name: '03094', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/baseboards/03094.JPG' },
    { name: '0W662', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/baseboards/0W662.JPG' },
    { name: '08667', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/baseboards/08667.JPG' },
    { name: '03098', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/baseboards/03098.JPG' },
    { name: '87618', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/baseboards/87618.JPG' },
    { name: '92132', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/baseboards/92132.JPG' },
    { name: 'L620A', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/baseboards/L620A.JPG' },
    // { name: 'L7620', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/baseboards/L7620.JPG' },
    { name: '00LK4', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/baseboards/00LK4.JPG' },
    { name: '92134', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/baseboards/92134.JPG' },
    { name: '92136', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/baseboards/92136.JPG' },
    { name: '05094', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/baseboards/05094.JPG' },
    { name: '05104', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/baseboards/05104.JPG' },
    { name: '0L430', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/baseboards/0L430.JPG' },
    { name: '0W620', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/baseboards/0W620.JPG' },
    { name: '5708A', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/baseboards/0708A.JPG' },
    { name: '72136', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/baseboards/72136.JPG' },
    { name: 'CR684', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/baseboards/CR684.JPG' },
    { name: 'WB629', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/baseboards/WB629.JPG' },
    { name: '05709', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/baseboards/05709.JPG' },
    { name: '87628', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/baseboards/87628.JPG' },
    { name: '92133', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/baseboards/92133.JPG' },
    { name: '5204A', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/baseboards/5204A.JPG' },
    { name: '1236B', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/baseboards/1236B.JPG' },
    { name: '01236', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/baseboards/01236.JPG' },
    { name: '0W618', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/baseboards/0W618.JPG' },
    { name: 'GM022', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/baseboards/GM022.JPG' },
    { name: 'LW647', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/baseboards/LW647.JPG' },
    { name: 'WM658', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/baseboards/WM658.JPG' },
    { name: '0E163', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/baseboards/0E163.JPG' },
    { name: '72137', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/baseboards/72137.JPG' },
    { name: '92137', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/baseboards/92137.JPG' },
  ];

  const caseOptions = [
    { name: 'L366A', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/casing/L366A.JPG' },
    { name: 'L356D', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/casing/L356D.JPG' },
    { name: '0L324', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/casing/0L324.JPG' },
    { name: '0W473', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/casing/OW473.JPG' },
    { name: '07356', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/casing/07356.JPG' },
    { name: '07327', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/casing/07327.JPG' },
    { name: '0W316', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/casing/0W316.JPG' },
    { name: '0W327', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/casing/0W327.JPG' },
    { name: '0W366', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/casing/0W366.JPG' },
    { name: '0W356', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/casing/0W356.JPG' },
    { name: '08581', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/casing/08581.JPG' },
    { name: '07366', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/casing/07366.JPG' },
    { name: 'L332A', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/casing/L332A.JPG' },
    { name: 'WM321', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/casing/WM321.JPG' },
    { name: 'WM324', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/casing/WM324.JPG' },
    { name: 'WM332', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/casing/WM332.JPG' },
    { name: '0W453', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/casing/0W453.JPG' },
    { name: '7453C', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/casing/7453C.JPG' },
    { name: '05092', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/casing/05092.JPG' },
    { name: 'D356A', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/casing/D356A.JPG' },
    { name: 'L492A', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/casing/L492A.JPG' },
    { name: '87362', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/casing/87362.JPG' },
    { name: '07351', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/casing/07351.JPG' },
    { name: '90472', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/casing/90472.JPG' },
    { name: '07361', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/casing/07361.JPG' },
    { name: '0W351', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/casing/0W351.JPG' },
    { name: '08587', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/casing/08587.JPG' },
    { name: '278CC', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/casing/278CC.JPG' },
    { name: '80644', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/casing/80644.JPG' },
    { name: '07714', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/casing/07714.JPG' },
    { name: '08583', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/casing/08583.JPG' },
    { name: '0W433', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/casing/0W433.JPG' },
    { name: '07445', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/casing/07445.JPG' },
    { name: '00LK1', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/casing/00LK1.JPG' },
    { name: '02488', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/casing/02488.JPG' },
    { name: '93285', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/casing/93285.JPG' },
    { name: 'DP380', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/casing/DP380.JPG' },
    { name: '0P380', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/casing/0P380.JPG' },
    { name: '84424', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/casing/84424.JPG' },
    { name: '7444A', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/casing/7444A.JPG' },
    { name: '0L444', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/casing/0L444.JPG' },
    { name: '0W360', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/casing/0W360.JPG' },
    { name: '0W445', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/casing/0W445.JPG' },
    { name: '08648', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/casing/08648.JPG' },
  ];

  if(label?.includes('Base')) {
    return (
      <div style={{ marginBottom: '2rem', padding: '1.5rem', backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #dee2e6' }}>
        <div 
          onClick={() => setShowOptions(!showOptions)}
          style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            cursor: 'pointer',
            userSelect: 'none'
          }}
        >
          <div>
            <h4 style={{ margin: '0 0 0.25rem 0', fontSize: '1rem', fontWeight: '600', color: '#333' }}>
              {label}
              {value && (
                <span style={{ color: '#007bff', fontSize: '0.9rem', fontWeight: 'normal', marginLeft: '0.5rem' }}>
                  ({value})
                </span>
              )}
            </h4>
            <p style={{ fontSize: '0.9rem', color: '#666', margin: 0 }}>
              Choose trim style
            </p>
          </div>
          <span style={{ fontSize: '1.5rem', color: '#666' }}>
            {showOptions ? '▼' : '^'}
          </span>
        </div>
        
        {showOptions && (
          <>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                gap: '1rem',
                marginTop: '1rem'
              }}
            >
              {baseOptions.map((option) => (
                <div
                  key={option.name}
                  onClick={() => {
                    if (!disabled) {
                      onChange(option.name);
                      setShowOptions(false);
                    }
                  }}
                  style={{
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    textAlign: 'center',
                    opacity: disabled ? 0.6 : 1,
                  }}
                >
                  <div
                    style={{
                      width: '100%',
                      height: '120px',
                      borderRadius: '8px',
                      border: value === option.name ? '3px solid #007bff' : '2px solid #ddd',
                      boxShadow: value === option.name ? '0 4px 12px rgba(0,123,255,0.3)' : '0 2px 4px rgba(0,0,0,0.1)',
                      transition: 'all 0.2s',
                      transform: value === option.name ? 'scale(1.05)' : 'scale(1)',
                      overflow: 'hidden',
                      position: 'relative',
                    }}
                  >
                    <img
                      src={option.image}
                      alt={option.name}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                      }}
                    />
                    {value === option.name && (
                      <div
                        style={{
                          position: 'absolute',
                          top: '5px',
                          right: '5px',
                          backgroundColor: '#007bff',
                          color: 'white',
                          borderRadius: '50%',
                          width: '24px',
                          height: '24px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '14px',
                          fontWeight: 'bold',
                        }}
                      >
                        ✓
                      </div>
                    )}
                  </div>
                  <p
                    style={{
                      margin: '0.5rem 0 0 0',
                      fontSize: '0.85rem',
                      color: value === option.name ? '#007bff' : '#333',
                      fontWeight: value === option.name ? '600' : '400',
                    }}
                  >
                    {option.name}
                  </p>
                </div>
              ))}
            </div>

            {disabled && (
              <p style={{ marginTop: '1rem', fontSize: '0.85rem', color: '#dc3545', fontStyle: 'italic' }}>
                Trim selection is locked. Contact administrator to make changes.
              </p>
            )}
          </>
        )}
      </div>
    );
  }
  else{
    return (
      <div style={{ marginBottom: '2rem', padding: '1.5rem', backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #dee2e6' }}>
        <div 
          onClick={() => setShowOptions(!showOptions)}
          style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            cursor: 'pointer',
            userSelect: 'none'
          }}
        >
          <div>
            <h4 style={{ margin: '0 0 0.25rem 0', fontSize: '1rem', fontWeight: '600', color: '#333' }}>
              {label}
              {value && (
                <span style={{ color: '#007bff', fontSize: '0.9rem', fontWeight: 'normal', marginLeft: '0.5rem' }}>
                  ({value})
                </span>
              )}
            </h4>
            <p style={{ fontSize: '0.9rem', color: '#666', margin: 0 }}>
              Choose trim style
            </p>
          </div>
          <span style={{ fontSize: '1.5rem', color: '#666' }}>
            {showOptions ? '▼' : '^'}
          </span>
        </div>
        
        {showOptions && (
          <>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                gap: '1rem',
                marginTop: '1rem'
              }}
            >
              {caseOptions.map((option) => (
                <div
                  key={option.name}
                  onClick={() => {
                    if (!disabled) {
                      onChange(option.name);
                      setShowOptions(false);
                    }
                  }}
                  style={{
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    textAlign: 'center',
                    opacity: disabled ? 0.6 : 1,
                  }}
                >
                  <div
                    style={{
                      width: '100%',
                      height: '120px',
                      borderRadius: '8px',
                      border: value === option.name ? '3px solid #007bff' : '2px solid #ddd',
                      boxShadow: value === option.name ? '0 4px 12px rgba(0,123,255,0.3)' : '0 2px 4px rgba(0,0,0,0.1)',
                      transition: 'all 0.2s',
                      transform: value === option.name ? 'scale(1.05)' : 'scale(1)',
                      overflow: 'hidden',
                      position: 'relative',
                    }}
                  >
                    <img
                      src={option.image}
                      alt={option.name}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                      }}
                    />
                    {value === option.name && (
                      <div
                        style={{
                          position: 'absolute',
                          top: '5px',
                          right: '5px',
                          backgroundColor: '#007bff',
                          color: 'white',
                          borderRadius: '50%',
                          width: '24px',
                          height: '24px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '14px',
                          fontWeight: 'bold',
                        }}
                      >
                        ✓
                      </div>
                    )}
                  </div>
                  <p
                    style={{
                      margin: '0.5rem 0 0 0',
                      fontSize: '0.85rem',
                      color: value === option.name ? '#007bff' : '#333',
                      fontWeight: value === option.name ? '600' : '400',
                    }}
                  >
                    {option.name}
                  </p>
                </div>
              ))}
            </div>

            {disabled && (
              <p style={{ marginTop: '1rem', fontSize: '0.85rem', color: '#dc3545', fontStyle: 'italic' }}>
                Trim selection is locked. Contact administrator to make changes.
              </p>
            )}
          </>
        )}
      </div>
    );
  }
  
}

function SelectionsForm({ 
  user, 
  userRole, 
  currentProject, 
  onNavigate, 
  db, 
  projects,
  onProjectChange
}) {
  const [formData, setFormData] = useState({});
  const [currentSection, setCurrentSection] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [lastSaved, setLastSaved] = useState(null);
  const canEdit = true; // Set based on your auth logic

  const sections = [
    { id: 'exterior-siding', title: '🏠 Exterior - Siding', icon: '🏠' },
    { id: 'exterior-roof', title: '🏠 Exterior - Roof', icon: '🏠' },
    { id: 'exterior-garage', title: '🚗 Exterior - Garage', icon: '🚗' },
    { id: 'exterior-brick', title: '🧱 Exterior - Brick/Stone', icon: '🧱' },
    { id: 'exterior-concrete', title: '🛤️ Exterior - Concrete', icon: '🛤️' },
    { id: 'exterior-deck', title: '♨️ Exterior - Deck', icon: '♨️' },
    { id: 'exterior-landscape', title: '🌱 Exterior - Landscaping', icon: '🌱' },
    { id: 'interior-fireplace', title: '🔥 Interior - Brick/Stone', icon: '🔥' },
    { id: 'interior-windows', title: '🚪 Interior - Windows/Doors', icon: '🚪' },
    { id: 'interior-basement', title: '🏚️ Interior - Basement', icon: '🏚️' },
    { id: 'interior-stairs', title: '↗️ Interior - Stairs', icon: '↗️' },
    { id: 'interior-insulation', title: '🧊 Interior - Insulation', icon: '🧊' },
    { id: 'interior-mechanicals', title: '🔧 Interior - Mechanicals', icon: '🔧' },
    { id: 'interior-appliances', title: '📺 Interior - Appliances', icon: '📺' },
    { id: 'interior-flooring', title: '🏠 Interior - Flooring', icon: '🏠' },
    { id: 'interior-tile', title: '🎨 Interior - Tile & Grout', icon: '🎨' },
    { id: 'interior-bathroom', title: '🚿 Interior - Bathroom', icon: '🚿' },
    { id: 'interior-kitchen', title: '🍳 Interior - Kitchen', icon: '🍳' },
    { id: 'interior-cabinets', title: '🗄️ Interior - Features', icon: '🗄️' },
    { id: 'interior-walls', title: '🎨 Interior - Walls', icon: '🎨' }
  ];

  const handleChange = (field, value) => {
    if (!canEdit) return;
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const nextSection = async () => {
    if (currentSection < sections.length - 1) {
      // Save current data before moving to next section
      await saveFormData(formData);
      setCurrentSection(currentSection + 1);
      window.scrollTo(0, 0);
    }
  };

  const prevSection = () => {
    if (currentSection > 0) {
      setCurrentSection(currentSection - 1);
      window.scrollTo(0, 0);
    }
  };

  const handleSubmit = async () => {
    if (!db || !currentProject?.id) {
      alert('No project selected');
      return;
    }
    
    setIsSaving(true);
    try {
      const { collection, doc, setDoc } = await import('firebase/firestore');
      const selectionsRef = doc(db, 'projects', currentProject.id, 'selections', 'formData');
      
      await setDoc(selectionsRef, {
        ...formData,
        lastModified: new Date(),
        modifiedBy: user?.uid || 'unknown',
        submittedAt: new Date(),
        status: 'submitted'
      }, { merge: true });
      
      setLastSaved(new Date());
      setSaveMessage('Selections submitted successfully!');
      alert('Your selections have been submitted successfully!');
      
      // Optionally navigate back to calendar
      if (onNavigate) {
        setTimeout(() => {
          onNavigate('calendar');
        }, 1500);
      }
    } catch (error) {
      console.error('Error submitting selections:', error);
      alert('Failed to submit selections. Please try again.');
      setSaveMessage('Submission failed');
    } finally {
      setIsSaving(false);
    }
  };

  // Auto-save when form data changes (debounced)
  useEffect(() => {
    if (!canEdit || Object.keys(formData).length === 0) return;
    
    const timeoutId = setTimeout(() => {
      saveFormData(formData);
    }, 2000); // Auto-save after 2 seconds of no changes
    
    return () => clearTimeout(timeoutId);
  }, [formData, canEdit]);

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showGableOptions, setShowGableOptions] = useState(true);
  const [showCorbelOptions, setShowCorbelOptions] = useState(true);
  const [showShingleColorOptions, setShowShingleColorOptions] = useState(true);
  const [showExteriorDoorColorOptions, setShowExteriorDoorColorOptions] = useState(true);
  const [showDeckColorOptions, setShowDeckColorOptions] = useState(true);
  const [showDoorOptions, setShowDoorOptions] = useState(true);
  const [showOutdoorDoorOptions, setShowOutdoorDoorOptions] = useState(true);
  const [showDoorHingeOptions, setShowDoorHingeOptions] = useState(true);
  const [showMirrorOptions, setShowMirrorOptions] = useState(true);
  const [showCeilingFanOptions, setShowCeilingFanOptions] = useState(true);
  const [showGarageDoorStyleOptions, setShowGarageDoorStyleOptions] = useState(true);
  const [showStainColorOptions, setShowStainColorOptions] = useState(true);
  const [showAluminumRailingOptions, setShowAluminumRailingOptions] = useState(true);
  const [showExteriorDoorGlass, setShowExteriorDoorGlass] = useState(false);
  const handleImages = {
    Round: {
        'Matte Black': 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/door-handles/knob-matte-black-front.jpg',
        'Brushed Black': 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/door-handles/knob-brushed-front.jpg',
        'Shiny Black': 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/door-handles/knob-black-front-whole.jpg',
        'Matte Gold': 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/door-handles/knob-matte-gold-back.jpg',
        'Shiny Gold': 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/door-handles/knob-shiny-gold-front-whole.jpg',
        'Polished Chrome': 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/door-handles/knob-matte-silver-front-whole.jpg',
        'Polished Silver': 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/door-handles/knob-shiny-silver-front.jpg',
    },
    
    Lever: {
        'Matte Black': 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/door-handles/lever-black-front-angled.jpg',
        'Brushed Black': 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/door-handles/lever-brushed-front.jpg',
        'Shiny Black': 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/door-handles/lever-black-front-straight.jpg',
        'Matte Gold': 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/door-handles/lever-gold-back.jpg',
        'Shiny Gold': 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/door-handles/lever-gold-front.jpg',
        'Polished Chrome': 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/door-handles/lever-silver-front-angled.jpg',
        'Polished Silver': 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/door-handles/lever-silver-front-straight.jpg',
    },
    
    'Wavy Lever': {
        'Matte Black': 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/door-handles/wavy-lever-matte-black-front.jpg',
        'Brushed Black': 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/door-handles/wavy-lever-brushed-front.jpg',
        'Shiny Black': 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/door-handles/wavy-lever-black-back-whole.jpg',
        'Matte Gold': 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/door-handles/wavy-lever-gold-back-whole.jpg',
        'Shiny Gold': 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/door-handles/wavy-lever-gold-front-whole.jpg',
        'Polished Chrome': 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/door-handles/wavy-lever-matte-silver-front-whole.jpg',
        'Polished Silver': 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/door-handles/wavy-lever-silver-front-whole.jpg',
    }
  };

  let selectedHandleImage =
    formData.handleStyle && formData.handleColor
        ? handleImages[formData.handleStyle]?.[formData.handleColor]
        : null;

  const renderSection = () => {
    const section = sections[currentSection];
    
    switch(section.id) {
      case 'exterior-siding':
        return (
          <div>
            {/* Siding Selector Component */}
            <div style={{ marginBottom: '2rem' }}>
              <SidingSelector
                canEdit={canEdit}
                value={formData.exteriorSiding}
                onChange={(v) => handleChange('exteriorSiding', v)}
              />
            </div>

            {/* Additional Siding Options */}
            <CheckboxField label="Tapered" checked={formData.tapered} onChange={(v) => handleChange('tapered', v)} disabled={!canEdit} />
            <CheckboxField label="Stone Base" checked={formData.stoneBase} onChange={(v) => handleChange('stoneBase', v)} disabled={!canEdit} />
            <FormField label="Front Porch Post Color" value={formData.porchPostColor} onChange={(v) => handleChange('porchPostColor', v)} disabled={!canEdit} placeholder="Enter color" />
            <CheckboxField label="Stained" checked={formData.porchStained} onChange={(v) => handleChange('porchStained', v)} disabled={!canEdit} />
            <SelectField label="Front Porch Pad" value={formData.porchPad} onChange={(v) => handleChange('porchPad', v)} disabled={!canEdit} options={['Concrete', 'Composite']} />
            <CheckboxField label="Front Porch Railing" checked={formData.porchRailing} onChange={(v) => handleChange('porchRailing', v)} disabled={!canEdit} />
            <SelectField label="Front Porch Steps" value={formData.porchSteps} onChange={(v) => handleChange('porchSteps', v)} disabled={!canEdit} options={['Concrete', 'Composite']} />
            <SelectField label="Front Porch Step Length" value={formData.porchStepLength} onChange={(v) => handleChange('porchStepLength', v)} disabled={!canEdit} options={['Full', 'Half']} />
            <CheckboxField label="Gable End" checked={formData.gableEnd} onChange={(v) => handleChange('gableEnd', v)} disabled={!canEdit} />
            {formData.gableEnd && (
              <SelectField label="Gable End Color" value={formData.gableEndColor} onChange={(v) => handleChange('gableEndColor', v)} disabled={!canEdit} options={['White', 'Black', 'Cedar']} />
            )}
            {/* Gable Options Selector */}
            <div style={{ marginBottom: '2rem', padding: '1.5rem', backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #dee2e6' }}>
              <div 
                onClick={() => setShowGableOptions(!showGableOptions)}
                style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  userSelect: 'none'
                }}
              >
                <div>
                  <h4 style={{ margin: '0 0 0.25rem 0', fontSize: '1rem', fontWeight: '600', color: '#333' }}>
                    Gable Options
                    {formData.gableOptions && (
                      <span style={{ color: '#007bff', fontSize: '0.9rem', fontWeight: 'normal', marginLeft: '0.5rem' }}>
                        ({formData.gableOptions})
                      </span>
                    )}
                  </h4>
                  <p style={{ fontSize: '0.9rem', color: '#666', margin: 0 }}>
                    Choose a gable style for your home
                  </p>
                </div>
                <span style={{ fontSize: '1.5rem', color: '#666' }}>
                  {showGableOptions ? '⌄' : '^'}
                </span>
              </div>
              
              {showGableOptions && (
                <>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                      gap: '1rem',
                      marginTop: '1rem'
                    }}
                  >
                    {[
                      { name: 'R', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/gable/Gabel-R.jpg' },
                      { name: 'RT', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/gable/Gabel-RT.jpg' },
                      { name: 'S', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/gable/Gabel-S.jpg' },
                      { name: 'ST', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/gable/Gabel-ST.jpg' },
                      { name: 'RL', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/gable/Gabel-RL.jpg' },
                      { name: 'RTL', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/gable/RTL.jpg' },
                      { name: 'SL', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/gable/Gabel-SL.jpg' },
                      { name: 'STL', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/gable/Gabel-STL.jpg' },
                    ].map((option) => (
                      <div
                        key={option.name}
                        onClick={() => {
                          if (canEdit) {
                            handleChange('gableOptions', option.name);
                            setShowGableOptions(false);
                          }
                        }}
                        style={{
                          cursor: canEdit ? 'pointer' : 'not-allowed',
                          textAlign: 'center',
                          opacity: !canEdit ? 0.6 : 1,
                        }}
                      >
                        <div
                          style={{
                            width: '100%',
                            height: '120px',
                            borderRadius: '8px',
                            border: formData.gableOptions === option.name ? '3px solid #007bff' : '2px solid #ddd',
                            boxShadow: formData.gableOptions === option.name ? '0 4px 12px rgba(0,123,255,0.3)' : '0 2px 4px rgba(0,0,0,0.1)',
                            transition: 'all 0.2s',
                            transform: formData.gableOptions === option.name ? 'scale(1.05)' : 'scale(1)',
                            overflow: 'hidden',
                            position: 'relative',
                          }}
                        >
                          <img
                            src={option.image}
                            alt={option.name}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            }}
                          />
                          {formData.gableOptions === option.name && (
                            <div
                              style={{
                                position: 'absolute',
                                top: '5px',
                                right: '5px',
                                backgroundColor: '#007bff',
                                color: 'white',
                                borderRadius: '50%',
                                width: '24px',
                                height: '24px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '14px',
                                fontWeight: 'bold',
                              }}
                            >
                              ✓
                            </div>
                          )}
                        </div>
                        <p
                          style={{
                            margin: '0.5rem 0 0 0',
                            fontSize: '0.85rem',
                            color: formData.gableOptions === option.name ? '#007bff' : '#333',
                            fontWeight: formData.gableOptions === option.name ? '600' : '400',
                          }}
                        >
                          {option.name}
                        </p>
                      </div>
                    ))}
                  </div>

                  {!canEdit && (
                    <p style={{ marginTop: '1rem', fontSize: '0.85rem', color: '#dc3545', fontStyle: 'italic' }}>
                      Gable selection is locked. Contact administrator to make changes.
                    </p>
                  )}
                </>
              )}
            </div>
            
            {/* Corbel Options Selector */}
            <div style={{ marginBottom: '2rem', padding: '1.5rem', backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #dee2e6' }}>
              <div 
                onClick={() => setShowCorbelOptions(!showCorbelOptions)}
                style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  userSelect: 'none'
                }}
              >
                <div>
                  <h4 style={{ margin: '0 0 0.25rem 0', fontSize: '1rem', fontWeight: '600', color: '#333' }}>
                    Corbel Options
                    {formData.corbelOptions && (
                      <span style={{ color: '#007bff', fontSize: '0.9rem', fontWeight: 'normal', marginLeft: '0.5rem' }}>
                        ({formData.corbelOptions})
                      </span>
                    )}
                  </h4>
                  <p style={{ fontSize: '0.9rem', color: '#666', margin: 0 }}>
                    Choose a corbel style for your home
                  </p>
                </div>
                <span style={{ fontSize: '1.5rem', color: '#666' }}>
                  {showCorbelOptions ? '⌄' : '^'}
                </span>
              </div>
              
              {showCorbelOptions && (
                <>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                      gap: '1rem',
                      marginTop: '1rem'
                    }}
                  >
                    {[
                      { name: 'C1', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/corbel/Corbel-C1.jpg' },
                      { name: 'C2', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/corbel/Corbel-C2.jpg' },
                      { name: 'CD 4', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/corbel/Corbel-CD-4.jpg' },
                      { name: 'CD 5', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/corbel/Corbel-CD-5.jpg' },
                      { name: 'CD 6', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/corbel/Corbel-CD-6.jpg' },
                      { name: 'R', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/corbel/Corbel-R.jpg' },
                      { name: 'RC', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/corbel/Corbel-RC.jpg' },
                    ].map((option) => (
                      <div
                        key={option.name}
                        onClick={() => {
                          if (canEdit) {
                            handleChange('corbelOptions', option.name);
                            setShowCorbelOptions(false);
                          }
                        }}
                        style={{
                          cursor: canEdit ? 'pointer' : 'not-allowed',
                          textAlign: 'center',
                          opacity: !canEdit ? 0.6 : 1,
                        }}
                      >
                        <div
                          style={{
                            width: '100%',
                            height: '150px',
                            borderRadius: '8px',
                            border: formData.corbelOptions === option.name ? '3px solid #007bff' : '2px solid #ddd',
                            boxShadow: formData.corbelOptions === option.name ? '0 4px 12px rgba(0,123,255,0.3)' : '0 2px 4px rgba(0,0,0,0.1)',
                            transition: 'all 0.2s',
                            transform: formData.corbelOptions === option.name ? 'scale(1.05)' : 'scale(1)',
                            overflow: 'hidden',
                            position: 'relative',
                          }}
                        >
                          <img
                            src={option.image}
                            alt={option.name}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            }}
                          />
                          {formData.corbelOptions === option.name && (
                            <div
                              style={{
                                position: 'absolute',
                                top: '5px',
                                right: '5px',
                                backgroundColor: '#007bff',
                                color: 'white',
                                borderRadius: '50%',
                                width: '24px',
                                height: '24px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '14px',
                                fontWeight: 'bold',
                              }}
                            >
                              ✓
                            </div>
                          )}
                        </div>
                        <p
                          style={{
                            margin: '0.5rem 0 0 0',
                            fontSize: '0.85rem',
                            color: formData.corbelOptions === option.name ? '#007bff' : '#333',
                            fontWeight: formData.corbelOptions === option.name ? '600' : '400',
                          }}
                        >
                          {option.name}
                        </p>
                      </div>
                    ))}
                  </div>

                  {!canEdit && (
                    <p style={{ marginTop: '1rem', fontSize: '0.85rem', color: '#dc3545', fontStyle: 'italic' }}>
                      Corbel selection is locked. Contact administrator to make changes.
                    </p>
                  )}
                </>
              )}
            </div>
            {/* <FormField label="Corbel Options" value={formData.corbelOptions} onChange={(v) => handleChange('corbelOptions', v)} disabled={!canEdit} placeholder="Describe corbel style" /> */}
            <CheckboxField label="Back Deck Over Hang" checked={formData.deckOverhang} onChange={(v) => handleChange('deckOverhang', v)} disabled={!canEdit} />
          </div>
        );

      case 'exterior-roof':
        return (
          <div>
            <SelectField label="Sheathing" value={formData.sheathing} onChange={(v) => handleChange('sheathing', v)} disabled={!canEdit} options={['1/2" OSB', '1/2" Plywood']} />
            <SelectField label="Ice and H2O Shield" value={formData.iceShield} onChange={(v) => handleChange('iceShield', v)} disabled={!canEdit} options={['Standard', 'Shark Skin']} />
            <SelectField label="Eave" value={formData.eave} onChange={(v) => handleChange('eave', v)} disabled={!canEdit} options={['1 Row', '2 Row', 'Valley']} />
            <SelectField label="Felt" value={formData.felt} onChange={(v) => handleChange('felt', v)} disabled={!canEdit} options={['Synthetic', 'Standard', 'Shark Skin']} />
            {/* Shingle Color Options Selector */}
            <div style={{ marginBottom: '2rem', padding: '1.5rem', backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #dee2e6' }}>
              <div 
                onClick={() => setShowShingleColorOptions(!showShingleColorOptions)}
                style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  userSelect: 'none'
                }}
              >
                <div>
                  <h4 style={{ margin: '0 0 0.25rem 0', fontSize: '1rem', fontWeight: '600', color: '#333' }}>
                    Shingle Color Options
                    {formData.shingleColorOptions && (
                      <span style={{ color: '#007bff', fontSize: '0.9rem', fontWeight: 'normal', marginLeft: '0.5rem' }}>
                        ({formData.shingleColorOptions})
                      </span>
                    )}
                  </h4>
                  <p style={{ fontSize: '0.9rem', color: '#666', margin: 0 }}>
                    Choose a shingle color for your home
                  </p>
                </div>
                <span style={{ fontSize: '1.5rem', color: '#666' }}>
                  {showShingleColorOptions ? '⌄' : '^'}
                </span>
              </div>
              
              {showShingleColorOptions && (
                <>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                      gap: '1rem',
                      marginTop: '1rem'
                    }}
                  >
                    {[
                      { name: 'Brownwood', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/shingle-color/brownwood.JPG' },
                      { name: 'Chateau Green', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/shingle-color/chateau-green.jpg' },
                      { name: 'Colonial State', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/shingle-color/colonial-state.jpg' },
                      { name: 'Desert Rose', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/shingle-color/desert-rose.jpg' },
                      { name: 'Driftwood', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/shingle-color/driftwood.jpg' },
                      { name: 'Estate Gray', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/shingle-color/estate-grey.jpg' },
                      { name: 'Midnight Plum', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/shingle-color/midnight-plum.JPG' },
                      { name: 'Onyx Black', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/shingle-color/onyx-black.jpg' },
                      { name: 'Peppercorn', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/shingle-color/peppercorn.jpg' },
                      { name: 'Sand Castle', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/shingle-color/sand-castle.jpg' },
                      { name: 'Sierra Gray', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/shingle-color/sierra-gray.jpg' },
                      { name: 'Slatestone Gray', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/shingle-color/slatestone-grey.jpg' },
                      { name: 'Teak', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/shingle-color/teak.jpg' },
                      { name: 'Terracotta', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/shingle-color/terra-cotta.jpg' },
                      { name: 'Williamsburg Gray', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/shingle-color/williamsburg-gray.JPG' },
                    ].map((option) => (
                      <div
                        key={option.name}
                        onClick={() => {
                          if (canEdit) {
                            handleChange('shingleColorOptions', option.name);
                            setShowShingleColorOptions(false);
                          }
                        }}
                        style={{
                          cursor: canEdit ? 'pointer' : 'not-allowed',
                          textAlign: 'center',
                          opacity: !canEdit ? 0.6 : 1,
                        }}
                      >
                        <div
                          style={{
                            width: '100%',
                            height: '120px',
                            borderRadius: '8px',
                            border: formData.shingleColorOptions === option.name ? '3px solid #007bff' : '2px solid #ddd',
                            boxShadow: formData.shingleColorOptions === option.name ? '0 4px 12px rgba(0,123,255,0.3)' : '0 2px 4px rgba(0,0,0,0.1)',
                            transition: 'all 0.2s',
                            transform: formData.shingleColorOptions === option.name ? 'scale(1.05)' : 'scale(1)',
                            overflow: 'hidden',
                            position: 'relative',
                          }}
                        >
                          <img
                            src={option.image}
                            alt={option.name}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            }}
                          />
                          {formData.shingleColorOptions === option.name && (
                            <div
                              style={{
                                position: 'absolute',
                                top: '5px',
                                right: '5px',
                                backgroundColor: '#007bff',
                                color: 'white',
                                borderRadius: '50%',
                                width: '24px',
                                height: '24px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '14px',
                                fontWeight: 'bold',
                              }}
                            >
                              ✓
                            </div>
                          )}
                        </div>
                        <p
                          style={{
                            margin: '0.5rem 0 0 0',
                            fontSize: '0.85rem',
                            color: formData.shingleColorOptions === option.name ? '#007bff' : '#333',
                            fontWeight: formData.shingleColorOptions === option.name ? '600' : '400',
                          }}
                        >
                          {option.name}
                        </p>
                      </div>
                    ))}
                  </div>

                  {!canEdit && (
                    <p style={{ marginTop: '1rem', fontSize: '0.85rem', color: '#dc3545', fontStyle: 'italic' }}>
                      Shingle Color selection is locked. Contact administrator to make changes.
                    </p>
                  )}
                </>
              )}
            </div>
            <CheckboxField label="Metal Accent" checked={formData.metalAccent} onChange={(v) => handleChange('metalAccent', v)} disabled={!canEdit} />
            <CheckboxField label="Standing Seam" checked={formData.standingSeam} onChange={(v) => handleChange('standingSeam', v)} disabled={!canEdit} />
            <CheckboxField label="Ridge Vent" checked={formData.ridgeVent} onChange={(v) => handleChange('ridgeVent', v)} disabled={!canEdit} />
            <CheckboxField label="4' Shingle Over" checked={formData.shingleOver} onChange={(v) => handleChange('shingleOver', v)} disabled={!canEdit} />
            <CheckboxField label="Cap Shingle Color Match Above" checked={formData.capMatch} onChange={(v) => handleChange('capMatch', v)} disabled={!canEdit} />
            <CheckboxField label="Drip Edge Color Match Aluminum" checked={formData.dripEdgeMatch} onChange={(v) => handleChange('dripEdgeMatch', v)} disabled={!canEdit} />
          </div>
        );

      case 'exterior-garage':
        return (
          <div>
            <SelectField label="Garage Size" value={formData.garageSize} onChange={(v) => handleChange('garageSize', v)} disabled={!canEdit} options={['2 Car', '3 Car', '4 Car']} />
            
            {/* Multiple door size options based on garage size */}
            {formData.garageSize === '2 Car' && (
              <>
                <SelectField label="Front Door Size" value={formData.frontDoorSize} onChange={(v) => handleChange('frontDoorSize', v)} disabled={!canEdit} options={['9×7', '9×8', '16×7', '16×8', '18×7', '18×8']} />
                <SelectField label="Side Door Size" value={formData.sideDoorSize} onChange={(v) => handleChange('sideDoorSize', v)} disabled={!canEdit} options={['None', '9×7', '9×8', '16×7', '16×8', '18×7', '18×8']} />
                <SelectField label="Rear Door Size" value={formData.rearDoorSize} onChange={(v) => handleChange('rearDoorSize', v)} disabled={!canEdit} options={['None', '9×7', '9×8', '16×7', '16×8', '18×7', '18×8']} />
              </>
            )}
            
            {formData.garageSize === '3 Car' && (
              <>
                <SelectField label="Front Door Size" value={formData.frontDoorSize} onChange={(v) => handleChange('frontDoorSize', v)} disabled={!canEdit} options={['9×7', '9×8', '16×7', '16×8', '18×7', '18×8']} />
                <SelectField label="Side Door Size" value={formData.sideDoorSize} onChange={(v) => handleChange('sideDoorSize', v)} disabled={!canEdit} options={['None', '9×7', '9×8', '16×7', '16×8', '18×7', '18×8']} />
                <SelectField label="Rear Door Size" value={formData.rearDoorSize} onChange={(v) => handleChange('rearDoorSize', v)} disabled={!canEdit} options={['None', '9×7', '9×8', '16×7', '16×8', '18×7', '18×8']} />
              </>
            )}
            
            {formData.garageSize === '4 Car' && (
              <>
                <SelectField label="Front Door Size" value={formData.frontDoorSize} onChange={(v) => handleChange('frontDoorSize', v)} disabled={!canEdit} options={['9×7', '9×8', '16×7', '16×8', '18×7', '18×8']} />
                <SelectField label="Side Door Size" value={formData.sideDoorSize} onChange={(v) => handleChange('sideDoorSize', v)} disabled={!canEdit} options={['None', '9×7', '9×8', '16×7', '16×8', '18×7', '18×8']} />
                <SelectField label="Rear Door Size" value={formData.rearDoorSize} onChange={(v) => handleChange('rearDoorSize', v)} disabled={!canEdit} options={['None', '9×7', '9×8', '16×7', '16×8', '18×7', '18×8']} />
              </>
            )}

            {/* Garage Door Style Options Selector */}
            <div style={{ marginBottom: '2rem', padding: '1.5rem', backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #dee2e6' }}>
              <div 
                onClick={() => setShowGarageDoorStyleOptions(!showGarageDoorStyleOptions)}
                style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  userSelect: 'none'
                }}
              >
                <div>
                  <h4 style={{ margin: '0 0 0.25rem 0', fontSize: '1rem', fontWeight: '600', color: '#333' }}>
                    Garage Door Style Options
                    {formData.garageDoorStyleOptions && (
                      <span style={{ color: '#007bff', fontSize: '0.9rem', fontWeight: 'normal', marginLeft: '0.5rem' }}>
                        ({formData.garageDoorStyleOptions})
                      </span>
                    )}
                  </h4>
                  <p style={{ fontSize: '0.9rem', color: '#666', margin: 0 }}>
                    Choose a garage door style for your home
                  </p>
                </div>
                <span style={{ fontSize: '1.5rem', color: '#666' }}>
                  {showGarageDoorStyleOptions ? '⌄' : '^'}
                </span>
              </div>
              
              {showGarageDoorStyleOptions && (
                <>
                  {/* Detail Images Section - Always show both side by side */}
                  <div style={{ marginTop: '1rem', marginBottom: '1.5rem' }}>
                    <h5 style={{ fontSize: '0.95rem', fontWeight: '600', color: '#333', marginBottom: '0.75rem' }}>
                      Detail Views:
                    </h5>
                    <div style={{ 
                      display: 'flex',
                      gap: '1rem',
                      maxWidth: '600px',
                    }}>
                      <div style={{ 
                        flex: 1,
                        border: '2px solid #ddd', 
                        borderRadius: '8px', 
                        overflow: 'hidden',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                      }}>
                        <img
                          src={'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/garage-doors/elegant-detail.JPG'}
                          alt={'Elegant detail'}
                          style={{
                            width: '100%',
                            height: 'auto',
                            display: 'block',
                          }}
                        />
                        <p style={{ 
                          textAlign: 'center', 
                          padding: '0.5rem', 
                          margin: 0, 
                          fontSize: '0.85rem',
                          fontWeight: '600',
                          backgroundColor: '#f8f9fa',
                          borderTop: '1px solid #ddd'
                        }}>
                          Elegant Detail
                        </p>
                      </div>
                      
                      <div style={{ 
                        flex: 1,
                        border: '2px solid #ddd', 
                        borderRadius: '8px', 
                        overflow: 'hidden',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                      }}>
                        <img
                          src={'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/garage-doors/traditinoal-detail.JPG'}
                          alt={'Traditional detail'}
                          style={{
                            width: '100%',
                            height: 'auto',
                            display: 'block',
                          }}
                        />
                        <p style={{ 
                          textAlign: 'center', 
                          padding: '0.5rem', 
                          margin: 0, 
                          fontSize: '0.85rem',
                          fontWeight: '600',
                          backgroundColor: '#f8f9fa',
                          borderTop: '1px solid #ddd'
                        }}>
                          Traditional Detail
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Style Options Grid */}
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                      gap: '1rem',
                      marginTop: '1rem'
                    }}
                  >
                    {[
                      { name: 'Short Elegant', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/garage-doors/short-elegant.JPG' },
                      { name: 'Short Traditional', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/garage-doors/short-traditional.JPG' },
                      { name: 'Long Elegant', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/garage-doors/long-elegant.JPG' },
                      { name: 'Long Traditional', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/garage-doors/long-traditional.JPG' },
                    ].map((option) => (
                      <div
                        key={option.name}
                        onClick={() => {
                          if (canEdit) {
                            handleChange('garageDoorStyleOptions', option.name);
                            setShowGarageDoorStyleOptions(false);
                          }
                        }}
                        style={{
                          cursor: canEdit ? 'pointer' : 'not-allowed',
                          textAlign: 'center',
                          opacity: !canEdit ? 0.6 : 1,
                        }}
                      >
                        <div
                          style={{
                            width: '100%',
                            height: '120px',
                            borderRadius: '8px',
                            border: formData.garageDoorStyleOptions === option.name ? '3px solid #007bff' : '2px solid #ddd',
                            boxShadow: formData.garageDoorStyleOptions === option.name ? '0 4px 12px rgba(0,123,255,0.3)' : '0 2px 4px rgba(0,0,0,0.1)',
                            transition: 'all 0.2s',
                            transform: formData.garageDoorStyleOptions === option.name ? 'scale(1.05)' : 'scale(1)',
                            overflow: 'hidden',
                            position: 'relative',
                          }}
                        >
                          <img
                            src={option.image}
                            alt={option.name}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            }}
                          />
                          {formData.garageDoorStyleOptions === option.name && (
                            <div
                              style={{
                                position: 'absolute',
                                top: '5px',
                                right: '5px',
                                backgroundColor: '#007bff',
                                color: 'white',
                                borderRadius: '50%',
                                width: '24px',
                                height: '24px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '14px',
                                fontWeight: 'bold',
                              }}
                            >
                              ✓
                            </div>
                          )}
                        </div>
                        <p
                          style={{
                            margin: '0.5rem 0 0 0',
                            fontSize: '0.85rem',
                            color: formData.garageDoorStyleOptions === option.name ? '#007bff' : '#333',
                            fontWeight: formData.garageDoorStyleOptions === option.name ? '600' : '400',
                          }}
                        >
                          {option.name}
                        </p>
                      </div>
                    ))}
                  </div>

                  {!canEdit && (
                    <p style={{ marginTop: '1rem', fontSize: '0.85rem', color: '#dc3545', fontStyle: 'italic' }}>
                      Garage Door Style selection is locked. Contact administrator to make changes.
                    </p>
                  )}
                </>
              )}
            </div>
            
            
            {/* Service Door Section */}
            <CheckboxField label="Service Door" checked={formData.serviceDoor} onChange={(v) => handleChange('serviceDoor', v)} disabled={!canEdit} />
            
            <SelectField label="Exterior Door Style" value={formData.serviceDoorStyle} onChange={(v) => handleChange('serviceDoorStyle', v)} disabled={!canEdit} options={['Standard', 'Steel', 'Fiberglass', 'Wood', 'Custom']} />
            
            <CheckboxField label="Singles" checked={formData.garageSingles} onChange={(v) => handleChange('garageSingles', v)} disabled={!canEdit} />
            <CheckboxField label="Bump Back" checked={formData.bumpBack} onChange={(v) => handleChange('bumpBack', v)} disabled={!canEdit} />
            {formData.bumpBack && (
              <>
                <SelectField label="Bump Back Size" value={formData.bumpBackSize} onChange={(v) => handleChange('bumpBackSize', v)} disabled={!canEdit} options={["1'", "2'", "3'"]} />
                <SelectField label="Bump Back Style" value={formData.bumpBackStyle} onChange={(v) => handleChange('bumpBackStyle', v)} disabled={!canEdit} options={['Chain', 'Belt', 'Side']} />
              </>
            )}
            <SelectField label="Garage Door Color Quality" value={formData.garageDoorQuality} onChange={(v) => handleChange('garageDoorQuality', v)} disabled={!canEdit} options={['Good', 'Better', 'Best']} />
            <FormField label="Windows" value={formData.garageWindows} onChange={(v) => handleChange('garageWindows', v)} disabled={!canEdit} placeholder="Describe window style" />
            <SelectField label="Trim Finish" value={formData.trimFinish} onChange={(v) => handleChange('trimFinish', v)} disabled={!canEdit} options={['White', 'Black', 'LP', 'Other']} />
            {formData.trimFinish === 'Other' && (
              <FormField label="Other Trim Finish" value={formData.trimFinishOther} onChange={(v) => handleChange('trimFinishOther', v)} disabled={!canEdit} />
            )}
            <CheckboxField label="Keypad Exterior" checked={formData.keypadExterior} onChange={(v) => handleChange('keypadExterior', v)} disabled={!canEdit} />
            <CheckboxField label="Aluminum Wrap" checked={formData.aluminumWrap} onChange={(v) => handleChange('aluminumWrap', v)} disabled={!canEdit} />
            <SelectField label="Number of Openers" value={formData.numOpeners} onChange={(v) => handleChange('numOpeners', v)} disabled={!canEdit} options={['1', '2', '3']} />
          </div>
        );

      case 'exterior-brick':
        return (
          <div>
            <CheckboxField label='42" Height' checked={formData.brick42} onChange={(v) => handleChange('brick42', v)} disabled={!canEdit} />
            <MultiCheckbox label="Location" options={['Front', 'Back', 'Left Side', 'Right Side', 'All']} values={formData.brickLocation} onChange={(v) => handleChange('brickLocation', v)} disabled={!canEdit} />
            <FormField label="Color" value={formData.brickColor} onChange={(v) => handleChange('brickColor', v)} disabled={!canEdit} placeholder="Choose brick color" />
            <FormField label="Mortar Color" value={formData.mortarColor} onChange={(v) => handleChange('mortarColor', v)} disabled={!canEdit} placeholder="Choose mortar color" />
            <FormField label="Style" value={formData.brickStyle} onChange={(v) => handleChange('brickStyle', v)} disabled={!canEdit} placeholder="Choose brick style" />
            <CheckboxField label="Full" checked={formData.brickFull} onChange={(v) => handleChange('brickFull', v)} disabled={!canEdit} />
            <CheckboxField label="Mailbox Match" checked={formData.mailboxMatch} onChange={(v) => handleChange('mailboxMatch', v)} disabled={!canEdit} />
          </div>
        );

      case 'exterior-concrete':
        return (
          <div>
            <CheckboxField label="Patio Back" checked={formData.patioBack} onChange={(v) => handleChange('patioBack', v)} disabled={!canEdit} />
            {formData.patioBack && (
              <FormField label="Patio Size" value={formData.patioSize} onChange={(v) => handleChange('patioSize', v)} disabled={!canEdit} placeholder="Enter dimensions" />
            )}
            <FormField label="Garage Step" value={formData.garageStep} onChange={(v) => handleChange('garageStep', v)} disabled={!canEdit} placeholder="Describe step details" />
            <SelectField label="Driveway" value={formData.driveway} onChange={(v) => handleChange('driveway', v)} disabled={!canEdit} options={['2 Car Straight', '3 Car Straight', '3 Car Swoop', 'Other']} />
            {formData.driveway === 'Other' && (
              <FormField label="Other Driveway" value={formData.drivewayOther} onChange={(v) => handleChange('drivewayOther', v)} disabled={!canEdit} />
            )}
            <CheckboxField label="4×4 Service Door Pad" checked={formData.serviceDoorPad} onChange={(v) => handleChange('serviceDoorPad', v)} disabled={!canEdit} />
            <MultiCheckbox label="Options" options={['End Flare', 'Straight', 'Radius', 'Public Walk', 'Turn Around', 'Basketball Pad', 'Other']} values={formData.concreteOptions} onChange={(v) => handleChange('concreteOptions', v)} disabled={!canEdit} />
            {formData.concreteOptions?.includes('Other') && (
              <FormField label="Other Options" value={formData.concreteOptionsOther} onChange={(v) => handleChange('concreteOptionsOther', v)} disabled={!canEdit} />
            )}
            <CheckboxField label="Stamped" checked={formData.stamped} onChange={(v) => handleChange('stamped', v)} disabled={!canEdit} />
            {formData.stamped && (
              <FormField label="Stamped Style" value={formData.stampedStyle} onChange={(v) => handleChange('stampedStyle', v)} disabled={!canEdit} placeholder="Describe pattern" />
            )}
            <MultiCheckbox label="Concrete Options" options={['6 Bag', 'Rebar', 'Fiber', 'Other']} values={formData.concreteMix} onChange={(v) => handleChange('concreteMix', v)} disabled={!canEdit} />
          </div>
        );

      case 'exterior-deck':
        return (
          <div>
            <SelectField label="Decking Material" value={formData.deckingMaterial} onChange={(v) => handleChange('deckingMaterial', v)} disabled={!canEdit} options={['Composite', 'Wood']} />
            <FormField label="Details" value={formData.deckDetails} onChange={(v) => handleChange('deckDetails', v)} disabled={!canEdit} textarea placeholder="Deck design details" />
            
            {/* Deck Color/Material Options Selector */}
            {formData.deckingMaterial && (
              <div style={{ marginBottom: '2rem', padding: '1.5rem', backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #dee2e6' }}>
                <div 
                  onClick={() => setShowDeckColorOptions(!showDeckColorOptions)}
                  style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    userSelect: 'none'
                  }}
                >
                  <div>
                    <h4 style={{ margin: '0 0 0.25rem 0', fontSize: '1rem', fontWeight: '600', color: '#333' }}>
                      {formData.deckingMaterial === 'Composite' ? 'Composite Deck Options' : 'Deck Stain Options'}
                      {formData.deckColorOptions && (
                        <span style={{ color: '#007bff', fontSize: '0.9rem', fontWeight: 'normal', marginLeft: '0.5rem' }}>
                          ({formData.deckColorOptions})
                        </span>
                      )}
                    </h4>
                    <p style={{ fontSize: '0.9rem', color: '#666', margin: 0 }}>
                      {formData.deckingMaterial === 'Composite' ? 'Choose a composite deck style' : 'Choose a deck stain color for your home'}
                    </p>
                  </div>
                  <span style={{ fontSize: '1.5rem', color: '#666' }}>
                    {showDeckColorOptions ? '⌄' : '^'}
                  </span>
                </div>
                
                {showDeckColorOptions && formData.deckingMaterial === 'Composite' && (
                  <>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                        gap: '1rem',
                        marginTop: '1rem'
                      }}
                    >
                      {[
                        { name: 'Good Life - Escapes - Beach House', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/decking/good-escapes-beach-house.jpg' },
                        { name: 'Good Life - Escapes - Bungalow', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/decking/good-escapes-bugalow.jpg' },
                        { name: 'Good Life - Escapes - Cabana', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/decking/good-escapes-cabana.jpg' },
                        { name: 'Good Life - Escapes - Tuscan Villa', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/decking/good-escapes-tuscan-villa.jpg' },
                        { name: 'Good Life - Weekender - Cabin', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/decking/good-weekender-cabin.jpg' },
                        { name: 'Good Life - Weekender - Cottage', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/decking/good-weekender-cottage.jpg' },
                        { name: 'Sanctuary - Chai', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/decking/sanctuary-chai.jpg' },
                        { name: 'Sanctuary - Earl Grey', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/decking/sanctuary-earl-grey.jpg' },
                        { name: 'Sanctuary - Espresso', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/decking/sanctuary-espresso.jpg' },
                        { name: 'Sanctuary - Latte', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/decking/sanctuary-latte.jpg' },
                        { name: 'Sanctuary - Moringa', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/decking/sanctuary-moringa.jpg' },
                      ].map((option) => (
                        <div
                          key={option.name}
                          onClick={() => {
                            if (canEdit) {
                              handleChange('deckColorOptions', option.name);
                              setShowDeckColorOptions(false);
                            }
                          }}
                          style={{
                            cursor: canEdit ? 'pointer' : 'not-allowed',
                            textAlign: 'center',
                            opacity: !canEdit ? 0.6 : 1,
                          }}
                        >
                          <div
                            style={{
                              width: '100%',
                              height: '120px',
                              borderRadius: '8px',
                              border: formData.deckColorOptions === option.name ? '3px solid #007bff' : '2px solid #ddd',
                              boxShadow: formData.deckColorOptions === option.name ? '0 4px 12px rgba(0,123,255,0.3)' : '0 2px 4px rgba(0,0,0,0.1)',
                              transition: 'all 0.2s',
                              transform: formData.deckColorOptions === option.name ? 'scale(1.05)' : 'scale(1)',
                              overflow: 'hidden',
                              position: 'relative',
                            }}
                          >
                            <img
                              src={option.image}
                              alt={option.name}
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                              }}
                            />
                            {formData.deckColorOptions === option.name && (
                              <div
                                style={{
                                  position: 'absolute',
                                  top: '5px',
                                  right: '5px',
                                  backgroundColor: '#007bff',
                                  color: 'white',
                                  borderRadius: '50%',
                                  width: '24px',
                                  height: '24px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '14px',
                                  fontWeight: 'bold',
                                }}
                              >
                                ✓
                              </div>
                            )}
                          </div>
                          <p
                            style={{
                              margin: '0.5rem 0 0 0',
                              fontSize: '0.85rem',
                              color: formData.deckColorOptions === option.name ? '#007bff' : '#333',
                              fontWeight: formData.deckColorOptions === option.name ? '600' : '400',
                            }}
                          >
                            {option.name}
                          </p>
                        </div>
                      ))}
                    </div>

                    {!canEdit && (
                      <p style={{ marginTop: '1rem', fontSize: '0.85rem', color: '#dc3545', fontStyle: 'italic' }}>
                        Composite Deck selection is locked. Contact administrator to make changes.
                      </p>
                    )}
                  </>
                )}

                {showDeckColorOptions && formData.deckingMaterial === 'Wood' && (
                  <>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                        gap: '1rem',
                        marginTop: '1rem'
                      }}
                    >
                      {[
                        { name: 'Traditional Mahogany', code: '#6A3B2E'},
                        { name: 'Shagbark', code: '#9B7A5B' },
                        { name: 'Woodbriar', code: '#946C48' },
                        { name: 'Cheyenne Red', code: '#8A3C33' },
                        { name: 'Shagbark Brown', code: '#5C3A28' },
                        { name: 'Buckthorn', code: '#A4703A' },
                        { name: 'Yosemite Gold', code: '#C9A156' },
                        { name: 'Salem Red', code: '#A04332' },
                        { name: 'Canyon', code: '#97573D' },
                        { name: 'Mission Brown', code: '#4A3124' },
                        { name: 'Sahara', code: '#D6B87A' },
                        { name: 'Almond Tree', code: '#C2A77E' },
                        { name: 'Rock Rose', code: '#A85E54' },
                        { name: 'Ranchero Red', code: '#923D34' },
                        { name: 'Spicewood', code: '#875842' },
                        { name: 'Cedar', code: '#B76E40' },
                        { name: 'Sequoia', code: '#753E31' },
                        { name: 'Russet Brown', code: '#6A4633' },
                        { name: 'Fawn', code: '#C2A57D' },
                        { name: 'Ember', code: '#AA553D' },
                        { name: 'Brick', code: '#8E4A3C' },
                        { name: 'Cape Cod Red', code: '#8C3E36' },
                        { name: 'Pepperidge', code: '#9A5340' },
                        { name: 'Desert Wood', code: '#B47A52' },
                        { name: 'Monterey Tan', code: '#C9AF8B' },
                        { name: 'Shade Tree', code: '#7D775C' },
                        { name: 'Acadia Blue', code: '#56788E' },
                        { name: 'Lodge Brown', code: '#4F372B' },
                        { name: 'Palmetto', code: '#5E6E55' },
                        { name: 'Greenbrier', code: '#4E6552' },
                        { name: 'Mallard Green', code: '#3E5041' },
                        { name: 'Espresso', code: '#3C2F25' },
                        { name: 'Belvedere Tan', code: '#C6A57D' },
                        { name: 'Cottonwood', code: '#B99872' },
                        { name: 'Orchard', code: '#977D5A' },
                        { name: 'Pineneedle', code: '#5C715B' },
                        { name: 'Cabin Brown', code: '#5A3D2B' },
                        { name: 'Sand Castle', code: '#D1B996' },
                        { name: 'Lichen', code: '#7A7D6B' },
                        { name: 'Blue Spruce', code: '#516A77' },
                        { name: 'Pine Cone', code: '#7A5C3D' },
                        { name: 'Summerhouse Beige', code: '#D5C0A4' },
                        { name: 'Cypress Moss', code: '#767458' },
                        { name: 'Wave Crest', code: '#7EA2B4' },
                        { name: 'Leather', code: '#5C4636' },
                        { name: 'Navajo White', code: '#F3E2C7' },
                        { name: 'Mushroom', code: '#A18E76' },
                        { name: 'River Rock', code: '#B9A98F' },
                        { name: 'Woodland', code: '#665B47' },
                        { name: 'Forest Dew', code: '#B3AF98' },
                        { name: 'Charcoal', code: '#4D4D4D' },
                        { name: 'Caribou', code: '#7C6651' },
                        { name: 'Woodsmoke Gray', code: '#9B9488' },
                        { name: 'Hudson Gray', code: '#7C7A70' },
                        { name: 'King\'s Canyon', code: '#6E5840' },
                        { name: 'Black Alder', code: '#3A2D23' },
                        { name: 'River Birch', code: '#A99374' },
                        { name: 'Stone', code: '#8B8578' },
                        { name: 'Meadowbrook', code: '#8EA293' },
                        { name: 'Driftwood', code: '#9D8C78' },
                        { name: 'Misty Mauve', code: '#A98B89' },
                        { name: 'Flagstone', code: '#B6ADA0' },
                        { name: 'Juniper Blue', code: '#5A7D8C' },
                        { name: 'Mercury', code: '#868B8F' },
                        { name: 'Tobacco', code: '#6C4A32' },
                        { name: 'Wet Clay', code: '#9A7561' },
                        { name: 'Chesapeake', code: '#7D8A8D' },
                        { name: 'Traditional Stone Hedge', code: '#6E716A' },
                        { name: 'Smoke Tree', code: '#975F46' },
                        { name: 'Antique Gray', code: '#C1BAB2' },
                        { name: 'Shale', code: '#898276' },
                        { name: 'Gray Birch', code: '#B6B1A3' },
                      ].map((option) => (
                        <div
                          key={option.name}
                          onClick={() => {
                            if (canEdit) {
                              handleChange('deckColorOptions', option.name);
                              setShowDeckColorOptions(false);
                            }
                          }}
                          style={{
                            cursor: canEdit ? 'pointer' : 'not-allowed',
                            textAlign: 'center',
                            opacity: !canEdit ? 0.6 : 1,
                          }}
                        >
                          <div
                              style={{
                                  width: "100%",
                                  height: "80px",
                                  backgroundColor: option.code,
                                  borderRadius: "8px",
                                  border: formData.deckColorOptions === option.name ? "3px solid #007bff" : "2px solid #ddd",
                                  boxShadow: formData.deckColorOptions === option.name ? "0 4px 12px rgba(0,123,255,0.3)" : "0 2px 4px rgba(0,0,0,0.1)",
                                  transition: "all 0.2s",
                                  transform: formData.deckColorOptions === option.name ? "scale(1.05)" : "scale(1)",
                              }}
                          />
                          <p
                              style={{
                                  margin: "0.5rem 0 0 0",
                                  fontSize: "0.8rem",
                                  color: formData.deckColorOptions === option.name ? "#007bff" : "#333",
                                  fontWeight: formData.deckColorOptions === option.name ? "600" : "400",
                              }}
                          >
                              {option.name}
                          </p>
                          {formData.deckColorOptions === option.name && (
                              <span style={{ fontSize: "0.7rem", color: "#007bff" }}>✓ Selected</span>
                          )}
                      </div>
                      ))}
                    </div>

                    {!canEdit && (
                      <p style={{ marginTop: '1rem', fontSize: '0.85rem', color: '#dc3545', fontStyle: 'italic' }}>
                        Deck Color selection is locked. Contact administrator to make changes.
                      </p>
                    )}
                  </>
                )}
              </div>
            )}
            
            <SelectField label="Railing Material" value={formData.railingMaterial} onChange={(v) => handleChange('railingMaterial', v)} disabled={!canEdit} options={['Composite', 'Aluminum', 'Vinyl']} />
            {/* Aluminum Railing Options */}
            {formData.railingMaterial === 'Aluminum' && (
              <div style={{ marginBottom: '2rem', padding: '1.5rem', backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #dee2e6' }}>
                <div 
                  onClick={() => setShowAluminumRailingOptions(!showAluminumRailingOptions)}
                  style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    userSelect: 'none'
                  }}
                >
                  <div>
                    <h4 style={{ margin: '0 0 0.25rem 0', fontSize: '1rem', fontWeight: '600', color: '#333' }}>
                      Aluminum Railing Options
                      {formData.aluminumRailingStyle && formData.aluminumRailingColor && (
                        <span style={{ color: '#007bff', fontSize: '0.9rem', fontWeight: 'normal', marginLeft: '0.5rem' }}>
                          ({formData.aluminumRailingStyle} - {formData.aluminumRailingColor})
                        </span>
                      )}
                    </h4>
                    <p style={{ fontSize: '0.9rem', color: '#666', margin: 0 }}>
                      Choose railing style and color
                    </p>
                  </div>
                  <span style={{ fontSize: '1.5rem', color: '#666' }}>
                    {showAluminumRailingOptions ? '⌄' : '^'}
                  </span>
                </div>
                
                {showAluminumRailingOptions && (
                  <>
                    {/* Vertical or Straight Selection with Images */}
                    <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem', marginTop: '1rem' }}>
                      <div
                        onClick={() => {
                          if (canEdit) {
                            handleChange('aluminumRailingStyle', 'Verticable');
                            handleChange('aluminumRailingColor', '');
                          }
                        }}
                        style={{
                          flex: 1,
                          cursor: canEdit ? 'pointer' : 'not-allowed',
                          opacity: !canEdit ? 0.6 : 1,
                        }}
                      >
                        <div
                          style={{
                            border: formData.aluminumRailingStyle === 'Verticable' ? '3px solid #007bff' : '2px solid #ddd',
                            borderRadius: '8px',
                            overflow: 'hidden',
                            boxShadow: formData.aluminumRailingStyle === 'Verticable' ? '0 4px 12px rgba(0,123,255,0.3)' : '0 2px 4px rgba(0,0,0,0.1)',
                            transition: 'all 0.2s',
                            transform: formData.aluminumRailingStyle === 'Verticable' ? 'scale(1.05)' : 'scale(1)',
                            position: 'relative',
                          }}
                        >
                          <img
                            src={'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/decking/railing/verticable.jpg'}
                            alt={'Verticable Railing'}
                            style={{
                              width: '100%',
                              height: '150px',
                              objectFit: 'contain',
                            }}
                          />
                          {formData.aluminumRailingStyle === 'Verticable' && (
                            <div
                              style={{
                                position: 'absolute',
                                top: '5px',
                                right: '5px',
                                backgroundColor: '#007bff',
                                color: 'white',
                                borderRadius: '50%',
                                width: '24px',
                                height: '24px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '14px',
                                fontWeight: 'bold',
                              }}
                            >
                              ✓
                            </div>
                          )}
                        </div>
                        <p style={{
                          textAlign: 'center',
                          marginTop: '0.5rem',
                          fontSize: '0.9rem',
                          fontWeight: formData.aluminumRailingStyle === 'Verticable' ? '600' : '400',
                          color: formData.aluminumRailingStyle === 'Verticable' ? '#007bff' : '#333',
                        }}>
                          Verticable
                        </p>
                      </div>
                      
                      <div
                        onClick={() => {
                          if (canEdit) {
                            handleChange('aluminumRailingStyle', 'Straight');
                            handleChange('aluminumRailingColor', '');
                          }
                        }}
                        style={{
                          flex: 1,
                          cursor: canEdit ? 'pointer' : 'not-allowed',
                          opacity: !canEdit ? 0.6 : 1,
                        }}
                      >
                        <div
                          style={{
                            border: formData.aluminumRailingStyle === 'Straight' ? '3px solid #007bff' : '2px solid #ddd',
                            borderRadius: '8px',
                            overflow: 'hidden',
                            boxShadow: formData.aluminumRailingStyle === 'Straight' ? '0 4px 12px rgba(0,123,255,0.3)' : '0 2px 4px rgba(0,0,0,0.1)',
                            transition: 'all 0.2s',
                            transform: formData.aluminumRailingStyle === 'Straight' ? 'scale(1.05)' : 'scale(1)',
                            position: 'relative',
                          }}
                        >
                          <img
                            src={'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/decking/railing/straight.jpg'}
                            alt={'Straight Railing'}
                            style={{
                              width: '100%',
                              height: '150px',
                              objectFit: 'contain',
                            }}
                          />
                          {formData.aluminumRailingStyle === 'Straight' && (
                            <div
                              style={{
                                position: 'absolute',
                                top: '5px',
                                right: '5px',
                                backgroundColor: '#007bff',
                                color: 'white',
                                borderRadius: '50%',
                                width: '24px',
                                height: '24px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '14px',
                                fontWeight: 'bold',
                              }}
                            >
                              ✓
                            </div>
                          )}
                        </div>
                        <p style={{
                          textAlign: 'center',
                          marginTop: '0.5rem',
                          fontSize: '0.9rem',
                          fontWeight: formData.aluminumRailingStyle === 'Straight' ? '600' : '400',
                          color: formData.aluminumRailingStyle === 'Straight' ? '#007bff' : '#333',
                        }}>
                          Straight
                        </p>
                      </div>
                    </div>
                    
                    {/* Color Options - Show when style is selected */}
                    {formData.aluminumRailingStyle && (
                      <>
                        <h5 style={{ margin: '1.5rem 0 1rem 0', fontSize: '0.95rem', fontWeight: '600', color: '#333' }}>
                          Select Color
                        </h5>
                        <div
                          style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                            gap: '1rem',
                          }}
                        >
                          {[
                            { name: 'Black Fine Texture', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/decking/colors/Black-Fine-Texture.jpg' },
                            { name: 'Bronze Fine Texture', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/decking/colors/Bronze-Fine-Texture.jpg' },
                            { name: 'Brown Fine Texture', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/decking/colors/Brown-Fine-Texture.jpg' },
                            { name: 'Chocolate', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/decking/colors/Chocolate.jpg' },
                            { name: 'Gloss Beige', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/decking/colors/Gloss-Beige.jpg' },
                            { name: 'Gloss White', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/decking/colors/Gloss-White.jpg' },
                            { name: 'Grey Fine Texture', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/decking/colors/Grey-Fine-Texture.jpg' },
                            { name: 'Ninety Bronze', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/decking/colors/Ninety-Bronze.jpg' },
                            { name: 'Sandy Shore', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/decking/colors/Sandy-Shore.jpg' },
                            { name: 'Satin Black', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/decking/colors/Satin-Black.jpg' },
                            { name: 'Speckled Walnut', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/decking/colors/Speckled-Walnut.jpg' },
                            { name: 'White Fine Texture', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/decking/colors/White-Fine-Texture.jpg' },
                          ].map((option) => (
                            <div
                              key={option.name}
                              onClick={() => {
                                if (canEdit) {
                                  handleChange('aluminumRailingColor', option.name);
                                }
                              }}
                              style={{
                                cursor: canEdit ? 'pointer' : 'not-allowed',
                                textAlign: 'center',
                                opacity: !canEdit ? 0.6 : 1,
                              }}
                            >
                              <div
                                style={{
                                  width: '100%',
                                  height: '100px',
                                  borderRadius: '8px',
                                  border: formData.aluminumRailingColor === option.name ? '3px solid #007bff' : '2px solid #ddd',
                                  boxShadow: formData.aluminumRailingColor === option.name ? '0 4px 12px rgba(0,123,255,0.3)' : '0 2px 4px rgba(0,0,0,0.1)',
                                  transition: 'all 0.2s',
                                  transform: formData.aluminumRailingColor === option.name ? 'scale(1.05)' : 'scale(1)',
                                  overflow: 'hidden',
                                  position: 'relative',
                                }}
                              >
                                <img
                                  src={option.image}
                                  alt={option.name}
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                  }}
                                />
                                {formData.aluminumRailingColor === option.name && (
                                  <div
                                    style={{
                                      position: 'absolute',
                                      top: '5px',
                                      right: '5px',
                                      backgroundColor: '#007bff',
                                      color: 'white',
                                      borderRadius: '50%',
                                      width: '24px',
                                      height: '24px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontSize: '14px',
                                      fontWeight: 'bold',
                                    }}
                                  >
                                    ✓
                                  </div>
                                )}
                              </div>
                              <p
                                style={{
                                  margin: '0.5rem 0 0 0',
                                  fontSize: '0.8rem',
                                  color: formData.aluminumRailingColor === option.name ? '#007bff' : '#333',
                                  fontWeight: formData.aluminumRailingColor === option.name ? '600' : '400',
                                }}
                              >
                                {option.name}
                              </p>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                    
                    {!canEdit && (
                      <p style={{ marginTop: '1rem', fontSize: '0.85rem', color: '#dc3545', fontStyle: 'italic' }}>
                        Aluminum Railing selection is locked. Contact administrator to make changes.
                      </p>
                    )}
                  </>
                )}
              </div>
            )}
            <FormField label="Additional Details" value={formData.deckAdditional} onChange={(v) => handleChange('deckAdditional', v)} disabled={!canEdit} textarea />
          </div>
        );

      case 'exterior-landscape':
        return (
          <div>
            <CheckboxField label="Sod" checked={formData.sod} onChange={(v) => handleChange('sod', v)} disabled={!canEdit} />
            {formData.sod && (
              <MultiCheckbox 
                label="Sod Location" 
                options={['Front Side', 'Left Side', 'Right Side', 'Back', 'All']} 
                values={formData.sodLocation} 
                onChange={(v) => handleChange('sodLocation', v)} 
                disabled={!canEdit} 
              />
            )}
            
            <CheckboxField label="Over Seed" checked={formData.overSeed} onChange={(v) => handleChange('overSeed', v)} disabled={!canEdit} />
            {formData.overSeed && (
              <MultiCheckbox 
                label="Over Seed Location" 
                options={['Front Side', 'Left Side', 'Right Side', 'Back', 'All']} 
                values={formData.overSeedLocation} 
                onChange={(v) => handleChange('overSeedLocation', v)} 
                disabled={!canEdit} 
              />
            )}
            
            <CheckboxField label="Sprinklers" checked={formData.sprinklers} onChange={(v) => handleChange('sprinklers', v)} disabled={!canEdit} />
            {formData.sprinklers && (
              <MultiCheckbox 
                  label="Sprinklers Location" 
                  options={['Front', 'Left Side', 'Right Side', 'Back', 'All']} 
                  values={formData.sprinklersLocation} 
                  onChange={(v) => handleChange('sprinklersLocation', v)} 
                  disabled={!canEdit} 
                />
            )}
          </div>
        );

      case 'interior-fireplace':
        return (
          <div>
            <CheckboxField label="Fireplace" checked={formData.fireplace} onChange={(v) => handleChange('fireplace', v)} disabled={!canEdit} />
            {formData.fireplace && (
              <>
                <CheckboxField label="Stone" checked={formData.fireplaceStone} onChange={(v) => handleChange('fireplaceStone', v)} disabled={!canEdit} />
                <CheckboxField label="Shiplap" checked={formData.fireplaceShiplap} onChange={(v) => handleChange('fireplaceShiplap', v)} disabled={!canEdit} />
                    {formData.fireplaceShiplap && (
                        <>
                            <SelectField label="Shiplap Size" value={formData.fireplaceSize} onChange={(v) => handleChange('fireplaceSize', v)} disabled={!canEdit} options={['6"', '8"']} />
                        </>
                    )}
                <SelectField label="Box Size" value={formData.boxSize} onChange={(v) => handleChange('boxSize', v)} disabled={!canEdit} options={['48"', '54"', '60"', '72"']} />
                <CheckboxField 
                    label="Homeowner to Supply Box" 
                    checked={formData.homeownerSupplyBox} 
                    onChange={(v) => handleChange('homeownerSupplyBox', v)} 
                    disabled={!canEdit} 
                />
                <SelectField 
                  label="Paint or Stain" 
                  value={formData.paintOrStain} 
                  onChange={(v) => handleChange('paintOrStain', v)} 
                  disabled={!canEdit} 
                  options={['None', 'Paint', 'Stain']} 
                />

                {formData.paintOrStain === 'Paint' && (
                  <>
                    <SelectField 
                      label="Paint Color" 
                      value={formData.paintColor} 
                      onChange={(v) => handleChange('paintColor', v)} 
                      disabled={!canEdit} 
                      options={['White', 'Black', 'Other']} 
                    />
                    {formData.paintColor === 'Other' && (
                      <FormField 
                        label="Other Paint Color" 
                        value={formData.paintColorOther} 
                        onChange={(v) => handleChange('paintColorOther', v)} 
                        disabled={!canEdit} 
                      />
                    )}
                  </>
                )}

                {formData.paintOrStain === 'Stain' && (
                  <div style={{ marginBottom: '2rem', padding: '1.5rem', backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #dee2e6' }}>
                    <div 
                      onClick={() => setShowStainColorOptions(!showStainColorOptions)}
                      style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between',
                        cursor: 'pointer',
                        userSelect: 'none'
                      }}
                    >
                      <div>
                        <h4 style={{ margin: '0 0 0.25rem 0', fontSize: '1rem', fontWeight: '600', color: '#333' }}>
                          Box Stain Color Options
                          {formData.stainColor && (
                            <span style={{ color: '#007bff', fontSize: '0.9rem', fontWeight: 'normal', marginLeft: '0.5rem' }}>
                              ({formData.stainColor})
                            </span>
                          )}
                        </h4>
                        <p style={{ fontSize: '0.9rem', color: '#666', margin: 0 }}>
                          Choose a box stain for your home
                        </p>
                      </div>
                      <span style={{ fontSize: '1.5rem', color: '#666' }}>
                        {showStainColorOptions ? '⌄' : '^'}
                      </span>
                    </div>
                    
                    {showStainColorOptions && (
                      <>
                        <div
                          style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                            gap: '1rem',
                            marginTop: '1rem'
                          }}
                        >
                          {[
                            { name: 'Antique Jade', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/interior-stains/antique-jade.JPG' },
                            { name: 'Antique Walnut', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/interior-stains/antique-walnut.JPG' },
                            { name: 'Apple Cider', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/interior-stains/apple-cider.JPG' },
                            { name: 'Burgundy', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/interior-stains/burgundy.JPG' },
                            { name: 'Charcoal Gray', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/interior-stains/charcoal-gray.JPG' },
                            { name: 'Cherry Blossom', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/interior-stains/cherry-blossom.JPG' },
                            { name: 'Cinnamon Toast', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/interior-stains/cinnamon-toast.JPG' },
                            { name: 'Classic Black', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/interior-stains/classic-black.JPG' },
                            { name: 'Coffee', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/interior-stains/coffee.JPG' },
                            { name: 'Colonial Pine', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/interior-stains/colonial-pine.JPG' },
                            { name: 'Coral', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/interior-stains/coral.JPG' },
                            { name: 'Crimson', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/interior-stains/crimson.JPG' },
                            { name: 'Daffodil', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/interior-stains/daffodil.JPG' },
                            { name: 'Deep Ocean', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/interior-stains/deep-ocean.JPG' },
                            { name: 'Desert Sand', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/interior-stains/desert-sand.JPG' },
                            { name: 'Driftwood', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/interior-stains/driftwood.JPG' },
                            { name: 'Green Tea', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/interior-stains/green-tea.JPG' },
                            { name: 'Harvest Gold', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/interior-stains/harvest-gold.JPG' },
                            { name: 'Harvest Grain', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/interior-stains/harvest-grain.JPG' },
                            { name: 'Honeycomb', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/interior-stains/honeycomb.JPG' },
                            { name: 'Honeydew', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/interior-stains/honeydew.JPG' },
                            { name: 'Hunter Green', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/interior-stains/hunter-green.JPG' },
                            { name: 'Island Water', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/interior-stains/island-water.JPG' },
                            { name: 'Natural Beige', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/interior-stains/natural-beige.JPG' },
                            { name: 'Natural Peach', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/interior-stains/natural-peach.JPG' },
                            { name: 'Navy Blue', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/interior-stains/nevy-blue.JPG' },
                            { name: 'Onyx', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/interior-stains/onyx.JPG' },
                            { name: 'Pearl Gray', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/interior-stains/pearl-gray.JPG' },
                            { name: 'Perfectly Pink', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/interior-stains/perfectly-pink.JPG' },
                            { name: 'Pure White', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/interior-stains/pure-white.JPG' },
                            { name: 'Rosewood', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/interior-stains/rosewood.jpg' },
                            { name: 'Sangria', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/interior-stains/sangria.JPG' },
                            { name: 'Slate', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/interior-stains/slate.JPG' },
                            { name: 'Slate Blue', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/interior-stains/slate-blue.JPG' },
                            { name: 'Spice', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/interior-stains/spice.JPG' },
                            { name: 'Tangerine', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/interior-stains/tangerine.JPG' },
                            { name: 'Terracotta', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/interior-stains/terracotta.JPG' },
                            { name: 'Toffee', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/interior-stains/toffee.JPG' },
                            { name: 'Vermont Maple', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/interior-stains/vermont-maple.JPG' },
                            { name: 'White Frost', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/interior-stains/white-frost.JPG' },
                            { name: 'Woodrose', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/interior-stains/woodrose.JPG' },
                          ].map((option) => (
                            <div
                              key={option.name}
                              onClick={() => {
                                if (canEdit) {
                                  handleChange('stainColor', option.name);
                                  setShowStainColorOptions(false);
                                }
                              }}
                              style={{
                                cursor: canEdit ? 'pointer' : 'not-allowed',
                                textAlign: 'center',
                                opacity: !canEdit ? 0.6 : 1,
                              }}
                            >
                              <div
                                style={{
                                  width: '100%',
                                  height: '120px',
                                  borderRadius: '8px',
                                  border: formData.stainClor === option.name ? '3px solid #007bff' : '2px solid #ddd',
                                  boxShadow: formData.stainColor === option.name ? '0 4px 12px rgba(0,123,255,0.3)' : '0 2px 4px rgba(0,0,0,0.1)',
                                  transition: 'all 0.2s',
                                  transform: formData.stainColor === option.name ? 'scale(1.05)' : 'scale(1)',
                                  overflow: 'hidden',
                                  position: 'relative',
                                }}
                              >
                                <img
                                  src={option.image}
                                  alt={option.name}
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                  }}
                                />
                                {formData.stainColor === option.name && (
                                  <div
                                    style={{
                                      position: 'absolute',
                                      top: '5px',
                                      right: '5px',
                                      backgroundColor: '#007bff',
                                      color: 'white',
                                      borderRadius: '50%',
                                      width: '24px',
                                      height: '24px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontSize: '14px',
                                      fontWeight: 'bold',
                                    }}
                                  >
                                    ✓
                                  </div>
                                )}
                              </div>
                              <p
                                style={{
                                  margin: '0.5rem 0 0 0',
                                  fontSize: '0.85rem',
                                  color: formData.stainColor === option.name ? '#007bff' : '#333',
                                  fontWeight: formData.stainColor === option.name ? '600' : '400',
                                }}
                              >
                                {option.name}
                              </p>
                            </div>
                          ))}
                        </div>

                        {!canEdit && (
                          <p style={{ marginTop: '1rem', fontSize: '0.85rem', color: '#dc3545', fontStyle: 'italic' }}>
                            Stain Color selection is locked. Contact administrator to make changes.
                          </p>
                        )}
                      </>
                    )}
                  </div>
                )}
                <FormField label="Masonry" value={formData.masonry} onChange={(v) => handleChange('masonry', v)} disabled={!canEdit} textarea />
                <SelectField label="Chase" value={formData.chase} onChange={(v) => handleChange('chase', v)} disabled={!canEdit} options={['Brick', 'Masonry']} />
                <MultiCheckbox label="Additional Options" options={['Blower', 'Doors', 'Accessories']} values={formData.fireplaceOptions} onChange={(v) => handleChange('fireplaceOptions', v)} disabled={!canEdit} />
                <SelectField label="Material" value={formData.fireplaceMaterial} onChange={(v) => handleChange('fireplaceMaterial', v)} disabled={!canEdit} options={['Ceramic', 'Brick', 'Stone', 'Surround']} />
                <FormField label="Grout" value={formData.fireplaceGrout} onChange={(v) => handleChange('fireplaceGrout', v)} disabled={!canEdit} />
                <FormField label="Additional Details" value={formData.fireplaceAdditional} onChange={(v) => handleChange('fireplaceAdditional', v)} disabled={!canEdit} textarea />
              </>
            )}
            
          </div>
        );

      case 'interior-windows':
        return (
          <div>
            {/* Flexbox container for Multi-Checkbox and Images */}
            <div style={{ 
              display: 'flex', 
              gap: '2rem', 
              alignItems: 'flex-start',
              marginBottom: '1.5rem'
            }}>
              {/* LEFT SIDE: Multi-Checkbox */}
              <div style={{ flex: "1 1 280px", minWidth: "250px" }}>
                <MultiCheckbox 
                  label="Window Types" 
                  options={['Single Hung', 'Double Hung', 'Casement', 'Slider', 'Transom']} 
                  values={formData.windowTypes} 
                  onChange={(v) => handleChange('windowTypes', v)} 
                  disabled={!canEdit} 
                />
              </div>

              {/* RIGHT SIDE: Window Type Images */}
              <div style={{ 
                flex: "1 1 300px", 
                minWidth: "250px",
                textAlign: 'center'
              }}>
                {(formData.windowTypes?.includes('Double Hung') || formData.windowTypes?.includes('Casement')) ? (
                  <div>
                    <h5 style={{ fontSize: '0.95rem', fontWeight: '600', color: '#333', marginBottom: '0.75rem' }}>
                      Selected Window Details:
                    </h5>
                    <div style={{ 
                      display: 'flex',
                      gap: '1rem',
                      justifyContent: 'center',
                      flexWrap: 'wrap'
                    }}>
                      {formData.windowTypes?.includes('Double Hung') && (
                        <div style={{ 
                          flex: '1 1 150px',
                          maxWidth: '200px',
                          border: '2px solid #007bff', 
                          borderRadius: '8px', 
                          overflow: 'hidden',
                          boxShadow: '0 2px 8px rgba(0,123,255,0.2)'
                        }}>
                          <img
                            src={'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/windows/double-hung.png'}
                            alt={'Double Hung Window'}
                            style={{
                              width: '100%',
                              height: 'auto',
                              display: 'block',
                            }}
                          />
                          <p style={{ 
                            textAlign: 'center', 
                            padding: '0.5rem', 
                            margin: 0, 
                            fontSize: '0.85rem',
                            fontWeight: '600',
                            backgroundColor: '#f8f9fa',
                            borderTop: '1px solid #007bff'
                          }}>
                            Double Hung
                          </p>
                        </div>
                      )}
                      
                      {formData.windowTypes?.includes('Casement') && (
                        <div style={{ 
                          flex: '1 1 150px',
                          maxWidth: '200px',
                          border: '2px solid #007bff', 
                          borderRadius: '8px', 
                          overflow: 'hidden',
                          boxShadow: '0 2px 8px rgba(0,123,255,0.2)'
                        }}>
                          <img
                            src={'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/windows/casement.png'}
                            alt={'Casement Window'}
                            style={{
                              width: '100%',
                              height: 'auto',
                              display: 'block',
                            }}
                          />
                          <p style={{ 
                            textAlign: 'center', 
                            padding: '0.5rem', 
                            margin: 0, 
                            fontSize: '0.85rem',
                            fontWeight: '600',
                            backgroundColor: '#f8f9fa',
                            borderTop: '1px solid #007bff'
                          }}>
                            Casement
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div style={{
                    color: '#777',
                    fontStyle: 'italic',
                    marginTop: '2rem',
                  }}>
                    Select Double Hung or Casement to preview
                  </div>
                )}
              </div>
            </div>

            <FormField label="Location" value={formData.windowLocation} onChange={(v) => handleChange('windowLocation', v)} disabled={!canEdit} textarea placeholder="Describe window locations" />
            <CheckboxField label="Grills" checked={formData.grills} onChange={(v) => handleChange('grills', v)} disabled={!canEdit} />
            <CheckboxField label="Patio Door" checked={formData.patioDoor} onChange={(v) => handleChange('patioDoor', v)} disabled={!canEdit} />
            <SelectField label="Window Exterior Color" value={formData.windowExteriorColor} onChange={(v) => handleChange('windowExteriorColor', v)} disabled={!canEdit} options={['White', 'Black', 'Other']} />
            <SelectField label="Window Interior Color" value={formData.windowInteriorColor} onChange={(v) => handleChange('windowInteriorColor', v)} disabled={!canEdit} options={['White', 'Black', 'Other']} />
            <SelectField label="Indoor Door Style" value={formData.doorStyle} onChange={(v) => handleChange('doorStyle', v)} disabled={!canEdit} options={['Hollow Core', 'Solid Core', 'Glass']} />
            <SelectField label="Indoor Door Size" value={formData.doorSize} onChange={(v) => handleChange('doorSize', v)} disabled={!canEdit} options={['Standard', "8'", 'Other']} />
            {/* Door Options Selector */}
            <div style={{ marginBottom: '2rem', padding: '1.5rem', backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #dee2e6' }}>
              <div 
                onClick={() => setShowDoorOptions(!showDoorOptions)}
                style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  userSelect: 'none'
                }}
              >
                <div>
                  <h4 style={{ margin: '0 0 0.25rem 0', fontSize: '1rem', fontWeight: '600', color: '#333' }}>
                    Interior Door Options
                    {formData.doorOptions && (
                      <span style={{ color: '#007bff', fontSize: '0.9rem', fontWeight: 'normal', marginLeft: '0.5rem' }}>
                        ({formData.doorOptions})
                      </span>
                    )}
                  </h4>
                  <p style={{ fontSize: '0.9rem', color: '#666', margin: 0 }}>
                    Choose an indoor door design for your home
                  </p>
                </div>
                <span style={{ fontSize: '1.5rem', color: '#666' }}>
                  {showDoorOptions ? '⌄' : '^'}
                </span>
              </div>
              
              {showDoorOptions && (formData.doorStyle == 'Hollow Core' || formData.doorStyle == 'Solid Core') &&(
                <>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                      gap: '0rem',
                      marginTop: '.5rem'
                    }}
                  >
                    {[
                      { name: 'Caiman', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/interior-door/caiman.jpg' },
                      //{ name: 'Camden', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/interior-door/camden.JPG' },
                      { name: 'Carrara', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/interior-door/carrara.jpg' },
                      { name: 'Colonist', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/interior-door/colonist.jpg' },
                      //{ name: 'Conmore', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/interior-door/conmore.JPG' },
                      { name: 'Craftsman', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/interior-door/craftsman.jpg' },
                      //{ name: 'Princeton', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/interior-door/princeton.JPG' },
                      { name: 'Rockport', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/interior-door/rockport.jpg' },
                      { name: 'Sante Fe', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/interior-door/santa-fe.jpg' },
                      { name: 'Smooth Colonist', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/interior-door/smooth-colonist.jpg' },
                    ].map((option) => (
                      <div
                        key={option.name}
                        onClick={() => {
                          if (canEdit) {
                            handleChange('doorOptions', option.name);
                            setShowDoorOptions(false);
                          }
                        }}
                        style={{
                          cursor: canEdit ? 'pointer' : 'not-allowed',
                          textAlign: 'center',
                          opacity: !canEdit ? 0.6 : 1,
                        }}
                      >
                        <div
                          style={{
                            width: '100px',
                            height: '200px',
                            borderRadius: '8px',
                            border: formData.doorOptions === option.name ? '3px solid #007bff' : '2px solid #ddd',
                            boxShadow: formData.doorOptions === option.name ? '0 4px 12px rgba(0,123,255,0.3)' : '0 2px 4px rgba(0,0,0,0.1)',
                            transition: 'all 0.2s',
                            transform: formData.doorOptions === option.name ? 'scale(1.05)' : 'scale(1)',
                            overflow: 'hidden',
                            position: 'relative',
                          }}
                        >
                          <img
                            src={option.image}
                            alt={option.name}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            }}
                          />
                          {formData.doorOptions === option.name && (
                            <div
                              style={{
                                position: 'absolute',
                                top: '5px',
                                right: '5px',
                                backgroundColor: '#007bff',
                                color: 'white',
                                borderRadius: '50%',
                                width: '24px',
                                height: '24px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '14px',
                                fontWeight: 'bold',
                              }}
                            >
                              ✓
                            </div>
                          )}
                        </div>
                        <p
                          style={{
                            width: '100px',
                            // height: '200px',
                            // margin: '0.5rem 0 0 0',
                            fontSize: '0.85rem',
                            color: formData.doorOptions === option.name ? '#007bff' : '#333',
                            fontWeight: formData.doorOptions === option.name ? '600' : '400',
                          }}
                        >
                          {option.name}
                        </p>
                      </div>
                    ))}
                  </div>

                  {!canEdit && (
                    <p style={{ marginTop: '1rem', fontSize: '0.85rem', color: '#dc3545', fontStyle: 'italic' }}>
                      Door Option selection is locked. Contact administrator to make changes.
                    </p>
                  )}
                </>
              )}

              {showDoorOptions && formData.doorStyle == 'Glass' &&(
                <>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                      gap: '0rem',
                      marginTop: '.5rem'
                    }}
                  >
                    {[
                      { name: '5 Lite Clear', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/interior-door/5-lite-glass.jpg' },
                      { name: '1 Lite Clear', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/interior-door/1-lite-glass.jpg' },
                      { name: '1 Lite Frosted', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/interior-door/1-lite-frosted-glass.jpg' },
                      { name: '10 Lite Clear', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/interior-door/10-lite-glass.jpg' },
                      { name: '15 Lite Clear', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/interior-door/15-lite-glass.jpg' },
                      { name: '9 Lite Clear', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/interior-door/9-lite-clear.jpg' },
                      { name: '1 Lite Pantry', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/interior-door/1-lite-pantry.jpg' },
                      { name: '3 Lite Frosted', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/interior-door/3-lite-frosted.jpg' },
                      { name: '5 Lite Frosted', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/interior-door/5-lite-frosted.jpg' },
                    ].map((option) => (
                      <div
                        key={option.name}
                        onClick={() => {
                          if (canEdit) {
                            handleChange('doorOptions', option.name);
                            setShowDoorOptions(false);
                          }
                        }}
                        style={{
                          cursor: canEdit ? 'pointer' : 'not-allowed',
                          textAlign: 'center',
                          opacity: !canEdit ? 0.6 : 1,
                        }}
                      >
                        <div
                          style={{
                            width: '100px',
                            height: '200px',
                            borderRadius: '8px',
                            border: formData.doorOptions === option.name ? '3px solid #007bff' : '2px solid #ddd',
                            boxShadow: formData.doorOptions === option.name ? '0 4px 12px rgba(0,123,255,0.3)' : '0 2px 4px rgba(0,0,0,0.1)',
                            transition: 'all 0.2s',
                            transform: formData.doorOptions === option.name ? 'scale(1.05)' : 'scale(1)',
                            overflow: 'hidden',
                            position: 'relative',
                          }}
                        >
                          <img
                            src={option.image}
                            alt={option.name}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            }}
                          />
                          {formData.doorOptions === option.name && (
                            <div
                              style={{
                                position: 'absolute',
                                top: '5px',
                                right: '5px',
                                backgroundColor: '#007bff',
                                color: 'white',
                                borderRadius: '50%',
                                width: '24px',
                                height: '24px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '14px',
                                fontWeight: 'bold',
                              }}
                            >
                              ✓
                            </div>
                          )}
                        </div>
                        <p
                          style={{
                            width: '100px',
                            // height: '200px',
                            // margin: '0.5rem 0 0 0',
                            fontSize: '0.85rem',
                            color: formData.doorOptions === option.name ? '#007bff' : '#333',
                            fontWeight: formData.doorOptions === option.name ? '600' : '400',
                          }}
                        >
                          {option.name}
                        </p>
                      </div>
                    ))}
                  </div>

                  {!canEdit && (
                    <p style={{ marginTop: '1rem', fontSize: '0.85rem', color: '#dc3545', fontStyle: 'italic' }}>
                      Door Option selection is locked. Contact administrator to make changes.
                    </p>
                  )}
                </>
              )}
            </div>
            <SelectField label="Indoor Door Color" value={formData.doorColor} onChange={(v) => handleChange('doorColor', v)} disabled={!canEdit} options={['White', 'Black', 'Bronze', 'Brown', 'Cafe', 'Almond', 'Sandstone', 'Desert Tan', 'Slate Grey']} />

            {/* Outdoor Door Options Selector */}
            <div style={{ marginBottom: '2rem', padding: '1.5rem', backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #dee2e6' }}>
              <div 
                onClick={() => setShowOutdoorDoorOptions(!showOutdoorDoorOptions)}
                style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  userSelect: 'none'
                }}
              >
                <div>
                  <h4 style={{ margin: '0 0 0.25rem 0', fontSize: '1rem', fontWeight: '600', color: '#333' }}>
                    Exterior Door Options
                    {formData.outdoorDoorOptions && (
                      <span style={{ color: '#007bff', fontSize: '0.9rem', fontWeight: 'normal', marginLeft: '0.5rem' }}>
                        ({formData.outdoorDoorOptions})
                      </span>
                    )}
                  </h4>
                  <p style={{ fontSize: '0.9rem', color: '#666', margin: 0 }}>
                    Choose an exterior door design for your home
                  </p>
                </div>
                <span style={{ fontSize: '1.5rem', color: '#666' }}>
                  {showOutdoorDoorOptions ? '⌄' : '^'}
                </span>
              </div>
              
              {showOutdoorDoorOptions && (
                <>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                      gap: '0rem',
                      marginTop: '.5rem'
                    }}
                  >
                    {[
                      { name: 'Smooth Star', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/exterior-door/smooth-star.JPG' },
                      { name: 'High Definition', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/exterior-door/high-definition.JPG' },
                      { name: 'Traditions', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/exterior-door/traditions.JPG' },
                    ].map((option) => (
                      <div
                        key={option.name}
                        onClick={() => {
                          if (canEdit) {
                            handleChange('outdoorDoorOptions', option.name);
                            setShowDoorOptions(false);
                          }
                        }}
                        style={{
                          cursor: canEdit ? 'pointer' : 'not-allowed',
                          textAlign: 'center',
                          opacity: !canEdit ? 0.6 : 1,
                        }}
                      >
                        <div
                          style={{
                            width: '100px',
                            height: '200px',
                            borderRadius: '8px',
                            border: formData.outdoorDoorOptions === option.name ? '3px solid #007bff' : '2px solid #ddd',
                            boxShadow: formData.outdoorDoorOptions === option.name ? '0 4px 12px rgba(0,123,255,0.3)' : '0 2px 4px rgba(0,0,0,0.1)',
                            transition: 'all 0.2s',
                            transform: formData.outdoorDoorOptions === option.name ? 'scale(1.05)' : 'scale(1)',
                            overflow: 'hidden',
                            position: 'relative',
                          }}
                        >
                          <img
                            src={option.image}
                            alt={option.name}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            }}
                          />
                          {formData.outdoorDoorOptions === option.name && (
                            <div
                              style={{
                                position: 'absolute',
                                top: '5px',
                                right: '5px',
                                backgroundColor: '#007bff',
                                color: 'white',
                                borderRadius: '50%',
                                width: '24px',
                                height: '24px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '14px',
                                fontWeight: 'bold',
                              }}
                            >
                              ✓
                            </div>
                          )}
                        </div>
                        <p
                          style={{
                            width: '100px',
                            fontSize: '0.85rem',
                            color: formData.outdoorDoorOptions === option.name ? '#007bff' : '#333',
                            fontWeight: formData.outdoorDoorOptions === option.name ? '600' : '400',
                          }}
                        >
                          {option.name}
                        </p>
                      </div>
                    ))}
                  </div>

                  {!canEdit && (
                    <p style={{ marginTop: '1rem', fontSize: '0.85rem', color: '#dc3545', fontStyle: 'italic' }}>
                      Exterior Door Option selection is locked. Contact administrator to make changes.
                    </p>
                  )}
                </>
              )}
            </div>

            {/* Exterior Door Glass Selector - Only shown for Smooth Star or Traditions */}
            {(formData.outdoorDoorOptions === 'Smooth Star' || formData.outdoorDoorOptions === 'Traditions') && (
              <div style={{ marginBottom: '2rem', padding: '1.5rem', backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #dee2e6' }}>
                <div 
                  onClick={() => setShowExteriorDoorGlass(!showExteriorDoorGlass)}
                  style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    userSelect: 'none'
                  }}
                >
                  <div>
                    <h4 style={{ margin: '0 0 0.25rem 0', fontSize: '1rem', fontWeight: '600', color: '#333' }}>
                      Exterior Door Glass
                      {formData.exteriorDoorGlass && (
                        <span style={{ color: '#007bff', fontSize: '0.9rem', fontWeight: 'normal', marginLeft: '0.5rem' }}>
                          ({formData.exteriorDoorGlass})
                        </span>
                      )}
                    </h4>
                    <p style={{ fontSize: '0.9rem', color: '#666', margin: 0 }}>
                      Select a glass style for your exterior door
                    </p>
                  </div>
                  <span style={{ fontSize: '1.5rem', color: '#666' }}>
                    {showExteriorDoorGlass ? '⌄' : '^'}
                  </span>
                </div>
                
                {showExteriorDoorGlass && (
                  <>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                        gap: '1rem',
                        marginTop: '1rem'
                      }}
                    >
                      {[
                        { name: 'Chinchilla', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/exterior-door-glass/chinchilla.JPG' },
                        { name: 'Chord', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/exterior-door-glass/chord.JPG' },
                        { name: 'Granite', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/exterior-door-glass/granite.JPG' },
                        { name: 'Rainglass', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/exterior-door-glass/rainglass.JPG' },
                        { name: 'Reeded', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/exterior-door-glass/reeded.JPG' },
                        { name: 'Satin Etched', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/exterior-door-glass/satin-etched.JPG' },
                      ].map((option) => (
                        <div
                          key={option.name}
                          onClick={() => {
                            if (canEdit) {
                              handleChange('exteriorDoorGlass', option.name);
                            }
                          }}
                          style={{
                            cursor: canEdit ? 'pointer' : 'not-allowed',
                            textAlign: 'center',
                            opacity: !canEdit ? 0.6 : 1,
                          }}
                        >
                          <div
                            style={{
                              width: '100%',
                              height: '200px',
                              borderRadius: '8px',
                              border: formData.exteriorDoorGlass === option.name ? '3px solid #007bff' : '2px solid #ddd',
                              boxShadow: formData.exteriorDoorGlass === option.name ? '0 4px 12px rgba(0,123,255,0.3)' : '0 2px 4px rgba(0,0,0,0.1)',
                              transition: 'all 0.2s',
                              transform: formData.exteriorDoorGlass === option.name ? 'scale(1.05)' : 'scale(1)',
                              overflow: 'hidden',
                              position: 'relative',
                            }}
                          >
                            <img
                              src={option.image}
                              alt={option.name}
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                              }}
                            />
                            {formData.exteriorDoorGlass === option.name && (
                              <div
                                style={{
                                  position: 'absolute',
                                  top: '5px',
                                  right: '5px',
                                  backgroundColor: '#007bff',
                                  color: 'white',
                                  borderRadius: '50%',
                                  width: '24px',
                                  height: '24px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '14px',
                                  fontWeight: 'bold',
                                }}
                              >
                                ✓
                              </div>
                            )}
                          </div>
                          <p
                            style={{
                              margin: '0.5rem 0 0 0',
                              fontSize: '0.85rem',
                              color: formData.exteriorDoorGlass === option.name ? '#007bff' : '#333',
                              fontWeight: formData.exteriorDoorGlass === option.name ? '600' : '400',
                            }}
                          >
                            {option.name}
                          </p>
                        </div>
                      ))}
                    </div>

                    {!canEdit && (
                      <p style={{ marginTop: '1rem', fontSize: '0.85rem', color: '#dc3545', fontStyle: 'italic' }}>
                        Exterior Door Glass selection is locked. Contact administrator to make changes.
                      </p>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Exterior Door Color Options Selector */}
            <div style={{ marginBottom: '2rem', padding: '1.5rem', backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #dee2e6' }}>
              <div 
                onClick={() => setShowExteriorDoorColorOptions(!showExteriorDoorColorOptions)}
                style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  userSelect: 'none'
                }}
              >
                <div>
                  <h4 style={{ margin: '0 0 0.25rem 0', fontSize: '1rem', fontWeight: '600', color: '#333' }}>
                    Exterior Door Color Options
                    {formData.exteriorDoorColorOptions && (
                      <span style={{ color: '#007bff', fontSize: '0.9rem', fontWeight: 'normal', marginLeft: '0.5rem' }}>
                        ({formData.exteriorDoorColorOptions})
                      </span>
                    )}
                  </h4>
                  <p style={{ fontSize: '0.9rem', color: '#666', margin: 0 }}>
                    Choose a exterior door color for your home
                  </p>
                </div>
                <span style={{ fontSize: '1.5rem', color: '#666' }}>
                  {showExteriorDoorColorOptions ? '⌄' : '^'}
                </span>
              </div>
              
              {showExteriorDoorColorOptions && (
                <>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                      gap: '1rem',
                      marginTop: '1rem'
                    }}
                  >
                    {[
                      { name: 'Acorn', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/exterior-door-colors/acorn.JPG' },
                      { name: 'Alpine', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/exterior-door-colors/alpine.JPG' },
                      { name: 'Arctic Ice', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/exterior-door-colors/arctic-ice.JPG' },
                      { name: 'Autumn Harvest', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/exterior-door-colors/autumn-harvest.JPG' },
                      { name: 'Barley', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/exterior-door-colors/barley.JPG' },
                      { name: 'Cabernet', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/exterior-door-colors/cabernet.JPG' },
                      { name: 'Canyon', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/exterior-door-colors/canyon.JPG' },
                      { name: 'Chestnut', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/exterior-door-colors/chestnut.JPG' },
                      { name: 'Cypress', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/exterior-door-colors/cypress.JPG' },
                      { name: 'Driftwood', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/exterior-door-colors/driftwood.JPG' },
                      { name: 'Elk', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/exterior-door-colors/elk.JPG' },
                      { name: 'Estuary', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/exterior-door-colors/estuary.JPG' },
                      { name: 'Golden Wheat', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/exterior-door-colors/golden-wheat.JPG' },
                      { name: 'Granite', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/exterior-door-colors/granite.JPG' },
                      { name: 'Indigo', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/exterior-door-colors/indigo.JPG' },
                      { name: 'Mulberry', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/exterior-door-colors/mulberry.JPG' },
                      { name: 'New Earth', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/exterior-door-colors/new-earth.JPG' },
                      { name: 'Obsidian', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/exterior-door-colors/obsidian.JPG' },
                      { name: 'Onyx', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/exterior-door-colors/onyx.JPG' },
                      { name: 'Peregrine', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/exterior-door-colors/peragrine.JPG' },
                      { name: 'Raven', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/exterior-door-colors/raven.JPG' },
                      { name: 'Ruby Red', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/exterior-door-colors/ruby-red.JPG' },
                      { name: 'Rustic Clay', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/exterior-door-colors/rustic-clay.JPG' },
                      { name: 'Tumbleweed', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/exterior-door-colors/tumbleweed.JPG' },
                      { name: 'Wildflower Honey', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/exterior-door-colors/wildflower-honey.JPG' },
                    ].map((option) => (
                      <div
                        key={option.name}
                        onClick={() => {
                          if (canEdit) {
                            handleChange('exteriorDoorColorOptions', option.name);
                            setShowExteriorDoorColorOptions(false);
                          }
                        }}
                        style={{
                          cursor: canEdit ? 'pointer' : 'not-allowed',
                          textAlign: 'center',
                          opacity: !canEdit ? 0.6 : 1,
                        }}
                      >
                        <div
                          style={{
                            width: '100%',
                            height: '120px',
                            borderRadius: '8px',
                            border: formData.exteriorDoorColorOptions === option.name ? '3px solid #007bff' : '2px solid #ddd',
                            boxShadow: formData.exteriorDoorColorOptions === option.name ? '0 4px 12px rgba(0,123,255,0.3)' : '0 2px 4px rgba(0,0,0,0.1)',
                            transition: 'all 0.2s',
                            transform: formData.exteriorDoorColorOptions === option.name ? 'scale(1.05)' : 'scale(1)',
                            overflow: 'hidden',
                            position: 'relative',
                          }}
                        >
                          <img
                            src={option.image}
                            alt={option.name}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            }}
                          />
                          {formData.exteriorDoorColorOptions === option.name && (
                            <div
                              style={{
                                position: 'absolute',
                                top: '5px',
                                right: '5px',
                                backgroundColor: '#007bff',
                                color: 'white',
                                borderRadius: '50%',
                                width: '24px',
                                height: '24px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '14px',
                                fontWeight: 'bold',
                              }}
                            >
                              ✓
                            </div>
                          )}
                        </div>
                        <p
                          style={{
                            margin: '0.5rem 0 0 0',
                            fontSize: '0.85rem',
                            color: formData.exteriorDoorColorOptions === option.name ? '#007bff' : '#333',
                            fontWeight: formData.exteriorDoorColorOptions === option.name ? '600' : '400',
                          }}
                        >
                          {option.name}
                        </p>
                      </div>
                    ))}
                  </div>

                  {!canEdit && (
                    <p style={{ marginTop: '1rem', fontSize: '0.85rem', color: '#dc3545', fontStyle: 'italic' }}>
                      Exterior Door Color selection is locked. Contact administrator to make changes.
                    </p>
                  )}
                </>
              )}
            </div>

            {/* Door Hinge Selector */}
            <div style={{ marginBottom: '2rem', padding: '1.5rem', backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #dee2e6' }}>
              <div 
                onClick={() => setShowDoorHingeOptions(!showDoorHingeOptions)}
                style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  userSelect: 'none'
                }}
              >
                <div>
                  <h4 style={{ margin: '0 0 0.25rem 0', fontSize: '1rem', fontWeight: '600', color: '#333' }}>
                    Door Hinge Options
                    {formData.doorHingeOptions && (
                      <span style={{ color: '#007bff', fontSize: '0.9rem', fontWeight: 'normal', marginLeft: '0.5rem' }}>
                        ({formData.doorHingeOptions})
                      </span>
                    )}
                  </h4>
                  <p style={{ fontSize: '0.9rem', color: '#666', margin: 0 }}>
                    Choose a door hinge style for your home
                  </p>
                </div>
                <span style={{ fontSize: '1.5rem', color: '#666' }}>
                  {showDoorHingeOptions ? '⌄' : '^'}
                </span>
              </div>
              
              {showDoorHingeOptions && (
                <>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                      gap: '1rem',
                      marginTop: '1rem'
                    }}
                  >
                    {[
                      { name: 'Black', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/hinge/black-hinge.jpg' },
                      { name: 'Bronze', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/hinge/bronze-hinge.jpg' },
                      { name: 'Brushed', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/hinge/brown-hinge.jpg' },
                      //{ name: 'Chrome', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/hinge/chrome-hinge.jpg' },
                      { name: 'Gold', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/hinge/gold-hinge.jpg' },
                      //{ name: 'Nickel', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/hinge/nickel-hinge.jpg' },
                      { name: 'Stainless', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/hinge/silver-hinge.jpg' },
                    ].map((option) => (
                      <div
                        key={option.name}
                        onClick={() => {
                          if (canEdit) {
                            handleChange('doorHingeOptions', option.name);
                            setShowDoorHingeOptions(false);
                          }
                        }}
                        style={{
                          cursor: canEdit ? 'pointer' : 'not-allowed',
                          textAlign: 'center',
                          opacity: !canEdit ? 0.6 : 1,
                        }}
                      >
                        <div
                          style={{
                            width: '100%',
                            height: '120px',
                            borderRadius: '8px',
                            border: formData.doorHingeOptions === option.name ? '3px solid #007bff' : '2px solid #ddd',
                            boxShadow: formData.doorHingeOptions === option.name ? '0 4px 12px rgba(0,123,255,0.3)' : '0 2px 4px rgba(0,0,0,0.1)',
                            transition: 'all 0.2s',
                            transform: formData.doorHingeOptions === option.name ? 'scale(1.05)' : 'scale(1)',
                            overflow: 'hidden',
                            position: 'relative',
                          }}
                        >
                          <img
                            src={option.image}
                            alt={option.name}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'contain',
                            }}
                          />
                          {formData.doorHingeOptions === option.name && (
                            <div
                              style={{
                                position: 'absolute',
                                top: '5px',
                                right: '5px',
                                backgroundColor: '#007bff',
                                color: 'white',
                                borderRadius: '50%',
                                width: '24px',
                                height: '24px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '14px',
                                fontWeight: 'bold',
                              }}
                            >
                              ✓
                            </div>
                          )}
                        </div>
                        <p
                          style={{
                            margin: '0.5rem 0 0 0',
                            fontSize: '0.85rem',
                            color: formData.doorHingeOptions === option.name ? '#007bff' : '#333',
                            fontWeight: formData.doorHingeOptions === option.name ? '600' : '400',
                          }}
                        >
                          {option.name}
                        </p>
                      </div>
                    ))}
                  </div>

                  {!canEdit && (
                    <p style={{ marginTop: '1rem', fontSize: '0.85rem', color: '#dc3545', fontStyle: 'italic' }}>
                      Door Hinge selection is locked. Contact administrator to make changes.
                    </p>
                  )}
                </>
              )}
            </div>
            <CheckboxField label="Sidelites" checked={formData.sidelites} onChange={(v) => handleChange('sidelites', v)} disabled={!canEdit} />
            <SelectField label="Door Handle Style" value={formData.handleStyle} onChange={(v) => handleChange('handleStyle', v)} disabled={!canEdit} options={['Round', 'Lever', 'Wavy Lever']} />
            <SelectField label="Door Handle Color" value={formData.handleColor} onChange={(v) => handleChange('handleColor', v)} disabled={!canEdit} options={['Matte Black', 'Brushed Black', 'Shiny Black', 'Matte Gold','Shiny Gold', 'Polished Chrome','Polished Silver']} />
            <div style={{ marginTop: '1rem', textAlign: 'center' }}>
                {selectedHandleImage ? (
                    <img
                    src={selectedHandleImage}
                    alt={`${formData.handleStyle} - ${formData.handleColor}`}
                    style={{
                        width: '150px',
                        height: '150px',
                        objectFit: 'contain',
                        borderRadius: '8px',
                        border: '2px solid #ddd',
                        boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                    }}
                    />
                ) : (
                    <p style={{ color: '#666', fontSize: '0.9rem' }}>
                    Select a handle style and color to preview
                    </p>
                )}
            </div>
            <CheckboxField label="Pocket Door" checked={formData.pocketDoor} onChange={(v) => handleChange('pocketDoor', v)} disabled={!canEdit} />
            <CheckboxField label="Bi-Fold Door" checked={formData.bifoldDoor} onChange={(v) => handleChange('bifoldDoor', v)} disabled={!canEdit} />
            <CheckboxField label="Barn Door" checked={formData.barnDoor} onChange={(v) => handleChange('barnDoor', v)} disabled={!canEdit} />
            {formData.barnDoor && (
              <SelectField label="Barn Door Supplier" value={formData.doorSupplier} onChange={(v) => handleChange('doorSupplier', v)} disabled={!canEdit} options={['RVCG', 'Homeowner Supplied']} />
            )}        
            <FormField label="Trim Details (Crown, Chair)" value={formData.trimDetails} onChange={(v) => handleChange('trimDetails', v)} disabled={!canEdit} textarea />
          </div>
        );

      case 'interior-basement':
        return (
          <div>
            <SelectField label="Basement Style" value={formData.basementStyle} onChange={(v) => handleChange('basementStyle', v)} disabled={!canEdit} options={['Walkout Basement', 'Lookout Basement']} />
            <MultiCheckbox label="Options" options={['Finished', 'Pre Plumb']} values={formData.basementOptions} onChange={(v) => handleChange('basementOptions', v)} disabled={!canEdit} />
            <SelectField label="Height" value={formData.basementHeight} onChange={(v) => handleChange('basementHeight', v)} disabled={!canEdit} options={["8'", "9'", "10'"]} />
            <SelectField label="Waterproofing" value={formData.waterproofing} onChange={(v) => handleChange('waterproofing', v)} disabled={!canEdit} options={['10 Year', 'Lifetime', 'Damproofing', 'Pea Gravel']} />
            <SelectField label="Drain Tile" value={formData.drainTile} onChange={(v) => handleChange('drainTile', v)} disabled={!canEdit} options={['Exterior', 'Interior', 'Both']} />
            <SelectField label="Egress" value={formData.egress} onChange={(v) => handleChange('egress', v)} disabled={!canEdit} options={['Egress Window', 'Slider', 'Other']} />
            <CheckboxField label="Soffit Box Out Around HVAC" checked={formData.soffitBoxOut} onChange={(v) => handleChange('soffitBoxOut', v)} disabled={!canEdit} />
            <CheckboxField label="Finished Basement Bath" checked={formData.finishedBath} onChange={(v) => handleChange('finishedBath', v)} disabled={!canEdit} />
            {formData.finishedBath && (
              <>
                <SelectField label="Finished Basement Bath Tub" value={formData.basementTub} onChange={(v) => handleChange('basementTub', v)} disabled={!canEdit} options={['One Piece Shower', 'One Piece Tub', 'Tub with Tiled Surround']} />
                <SelectField label="Finished Basement Bath Sink" value={formData.basementSink} onChange={(v) => handleChange('basementSink', v)} disabled={!canEdit} options={['Oval', 'Rectangle']} />
              </>
            )}
            <CheckboxField label="Finished Bedroom" checked={formData.finishedBed} onChange={(v) => handleChange('finishedBed', v)} disabled={!canEdit} />
            {formData.finishedBed && (
              <SelectField label="Closet Options" value={formData.closetOptions} onChange={(v) => handleChange('closetOptions', v)} disabled={!canEdit} options={['French Doors', 'Standard', 'Bifold']} />
            )}
          </div>
        );

      case 'interior-stairs':
        return (
          <div>
            <CheckboxField label="Basement Stairway Finish" checked={formData.basementStairFinish} onChange={(v) => handleChange('basementStairFinish', v)} disabled={!canEdit} />
            <CheckboxField label="Bonus Stairway Finish" checked={formData.bonusStairFinish} onChange={(v) => handleChange('bonusStairFinish', v)} disabled={!canEdit} />
            <CheckboxField label="Carpet on the Stairs" checked={formData.stairCarpet} onChange={(v) => handleChange('stairCarpet', v)} disabled={!canEdit} />
            <CheckboxField label="Painted" checked={formData.stairPainted} onChange={(v) => handleChange('stairPainted', v)} disabled={!canEdit} />
            <CheckboxField label="Handrail" checked={formData.handrail} onChange={(v) => handleChange('handrail', v)} disabled={!canEdit} />
            {formData.handrail && (
              <>
                <SelectField label="Rails" value={formData.rails} onChange={(v) => handleChange('rails', v)} disabled={!canEdit} options={['Oak', 'Poplar']} />
                <SelectField label="Finish" value={formData.railFinish} onChange={(v) => handleChange('railFinish', v)} disabled={!canEdit} options={['Painted', 'Stained']} />
                {formData.railFinish === 'Painted' && (
                    <PaintColorSelector
                        label="Rail Paint Color"
                        value={formData.railPaintColor}
                        onChange={(v) => handleChange('railPaintColor', v)}
                        disabled={!canEdit}
                    />
                )}
                {formData.railFinish === 'Stained' && (
                    <PaintColorSelector
                        label="Rail Stain Color"
                        value={formData.railStainColor}
                        onChange={(v) => handleChange('railStainColor', v)}
                        disabled={!canEdit}
                    />
                )}
                <SelectField label="Handrail Bracket Color" value={formData.bracketColor} onChange={(v) => handleChange('bracketColor', v)} disabled={!canEdit} options={['Gold', 'Black', 'Bronze', 'Brushed']} />
              </>
            )}
            <SelectField label="Treads" value={formData.treads} onChange={(v) => handleChange('treads', v)} disabled={!canEdit} options={['Open Tread', 'Closed Tread']} />
            <SelectField label="Flooring" value={formData.stairFlooring} onChange={(v) => handleChange('stairFlooring', v)} disabled={!canEdit} options={['LVP', 'Wood', 'Carpet']} />
            <SelectField label="Metal" value={formData.stairMetal} onChange={(v) => handleChange('stairMetal', v)} disabled={!canEdit} options={['Aluminum', 'Wire']} />
            <SelectField label="Knull Post" value={formData.knullPost} onChange={(v) => handleChange('knullPost', v)} disabled={!canEdit} options={['Painted', 'Stained']} />
            <SelectField label="Balusters" value={formData.balusters} onChange={(v) => handleChange('balusters', v)} disabled={!canEdit} options={['Painted', 'Stained']} />
            <CheckboxField label="1/2 Walls with Cap" checked={formData.halfWalls} onChange={(v) => handleChange('halfWalls', v)} disabled={!canEdit} />
            {formData.halfWalls && (
              <SelectField label="Walls with Cap" value={formData.wallsCapFinish} onChange={(v) => handleChange('wallsCapFinish', v)} disabled={!canEdit} options={['Painted', 'Stained']} />
            )}
            <FormField label="Additional Details" value={formData.stairAdditional} onChange={(v) => handleChange('stairAdditional', v)} disabled={!canEdit} textarea />
          </div>
        );

      case 'interior-insulation':
        return (
          <div>
            <CheckboxField label="Insulation" checked={formData.insulation} onChange={(v) => handleChange('insulation', v)} disabled={!canEdit} />
            {formData.insulation && (
              <>
                <MultiCheckbox label="Insulation Options" options={['Common Wall Only', 'Entire Garage', 'Fire Taped', 'Primed', 'Painted']} values={formData.insulationOptions} onChange={(v) => handleChange('insulationOptions', v)} disabled={!canEdit} />
                <MultiCheckbox label="Ceiling Options" options={['Textured', 'Primed', 'Painted']} values={formData.ceilingOptions} onChange={(v) => handleChange('ceilingOptions', v)} disabled={!canEdit} />
                <CheckboxField label="Batted" checked={formData.batted} onChange={(v) => handleChange('batted', v)} disabled={!canEdit} />
                <SelectField label="Foam" value={formData.foam} onChange={(v) => handleChange('foam', v)} disabled={!canEdit} options={['Full Cavity', '1"']} />
                <SelectField label="Insulation Type" value={formData.insulationType} onChange={(v) => handleChange('insulationType', v)} disabled={!canEdit} options={['Attic Blown R40', 'Attic Blown R60', 'R13 W/ Vapor Barrier', 'R38 Kraft Faced', 'R50 (Unblowable Areas)']} />
                <CheckboxField label="Garage Insulation (R30)" checked={formData.garageInsulation} onChange={(v) => handleChange('garageInsulation', v)} disabled={!canEdit} />
                <SelectField label="Basement Insulation Type" value={formData.basementInsulation} onChange={(v) => handleChange('basementInsulation', v)} disabled={!canEdit} options={['R11 w/Vapor Barrier', 'R13', 'R15']} />
                <MultiCheckbox label="Additional Options" options={['Soundening Batted', 'Ceiling Batted', 'Drain Lines', 'Crawl Space']} values={formData.insulationAdditional} onChange={(v) => handleChange('insulationAdditional', v)} disabled={!canEdit} />
              </>
            )}
          </div>
        );

      case 'interior-mechanicals':
        return (
          <div>
            <SelectField label="Furnace" value={formData.furnace} onChange={(v) => handleChange('furnace', v)} disabled={!canEdit} options={['97%', '98%', 'Variable Speed']} />
            <SelectField label="Air Conditioning" value={formData.airConditioning} onChange={(v) => handleChange('airConditioning', v)} disabled={!canEdit} options={['Central Heat Pump', 'Cond. Pump']} />
            <SelectField label="Tons" value={formData.tons} onChange={(v) => handleChange('tons', v)} disabled={!canEdit} options={['3', '3.5', '4', 'Other']} />
            <FormField label="# of Units" type="number" value={formData.numUnits} onChange={(v) => handleChange('numUnits', v)} disabled={!canEdit} />
            <SelectField label="Zones" value={formData.zones} onChange={(v) => handleChange('zones', v)} disabled={!canEdit} options={['1', '2', '3']} />
            <SelectField label="Water Heater" value={formData.waterHeaterType} onChange={(v) => handleChange('waterHeaterType', v)} disabled={!canEdit} options={['Standard', '40', '50', '75', 'Tankless']} />
            <SelectField label="Water Softener" value={formData.waterSoftener} onChange={(v) => handleChange('waterSoftener', v)} disabled={!canEdit} options={['Supplied', 'Rented', 'Softener Loop']} />
            <SelectField label="Number Of Sumps" value={formData.numSumps} onChange={(v) => handleChange('numSumps', v)} disabled={!canEdit} options={['1', '2', '3']} />
            <CheckboxField label="Radon" checked={formData.radon} onChange={(v) => handleChange('radon', v)} disabled={!canEdit} />
            <CheckboxField label="Smoke Detectors" checked={formData.smokeDetectors} onChange={(v) => handleChange('smokeDetectors', v)} disabled={!canEdit} />
            <CheckboxField label="Generator" checked={formData.generator} onChange={(v) => handleChange('generator', v)} disabled={!canEdit} />
            <CheckboxField label="Micro or Hood Vent to Outside" checked={formData.ventOutside} onChange={(v) => handleChange('ventOutside', v)} disabled={!canEdit} />
            <CheckboxField label="Coax" checked={formData.coax} onChange={(v) => handleChange('coax', v)} disabled={!canEdit} />
            <CheckboxField label="Internet Cable" checked={formData.internetCable} onChange={(v) => handleChange('internetCable', v)} disabled={!canEdit} />
            <CheckboxField label="Fan Box" checked={formData.fanBox} onChange={(v) => handleChange('fanBox', v)} disabled={!canEdit} />
            <MultiCheckbox label="Recessed Lighting" options={['Kitchen', 'Bedroom', 'Master Closet', 'LR', 'Basement', 'Hallway', 'Bathrooms']} values={formData.recessedLighting} onChange={(v) => handleChange('recessedLighting', v)} disabled={!canEdit} />
            <FormField label="Additional Details" value={formData.mechanicalsAdditional} onChange={(v) => handleChange('mechanicalsAdditional', v)} disabled={!canEdit} textarea />
          </div>
        );

      case 'interior-appliances':
        return (
            <div>
            <SelectField
                label="Provided By"
                value={formData.appliancesProvider}
                onChange={(v) => handleChange('appliancesProvider', v)}
                disabled={!canEdit}
                options={['Homeowner Supplied', 'Best Buy Package']}
            />

            {formData.appliancesProvider === 'Best Buy Package' && (
                <div
                    style={{
                        width: '100%',
                        height: '500px',
                        borderRadius: '8px',
                        border: '3px solid #007bff',
                        boxShadow: '0 4px 12px rgba(0,123,255,0.3)',
                        transition: 'all 0.2s',
                        transform: 'scale(1.05)',
                        overflow: 'hidden',
                        position: 'relative',
                        marginTop: '1rem',
                    }}
                >
                    <img
                        src="https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/fridge/ge-fridge-side.jpg"
                        alt="Best Buy GE Side-By-Side Refrigerator"
                        style={{
                        width: '25%',
                        height: '100%',
                        objectFit: 'contain',
                        }}
                    />
                    <img
                        src="https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/fridge/ge-fridge-side-specs.jpg"
                        alt="Best Buy GE Side-By-Side Refrigerator Specifications"
                        style={{
                        width: '25%',
                        height: '100%',
                        objectFit: 'contain',
                        }}
                    />
                    <img
                        src="https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/oven/freestanding-double.jpg"
                        alt="Best Buy GE Freestanding Double Oven"
                        style={{
                        width: '25%',
                        height: '100%',
                        objectFit: 'contain',
                        }}
                    />
                    <img
                        src="https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/oven/freestanding-double-specs.jpg"
                        alt="Best Buy GE Freestanding Double Oven Specifications"
                        style={{
                        width: '25%',
                        height: '100%',
                        objectFit: 'contain',
                        }}
                    />
                </div>
            )}
            </div>
        );

      case 'interior-flooring':
        const rooms = [
          'Kitchen', 'Mud Room', 'Dinette', 'Dining Room', 'Great Room', 'LR/FR', 
          'Foyer', 'Study', 'Master Bedroom', 'Bedroom #2', 'Bedroom #3', 'Bedroom #4',
          '1st Floor Halls', '2nd Floor Halls', 'Other Halls', 'Laundry', 
          'Master Bath', 'Bath #2', 'Bath #3', 'Bath #4', 'Basement', 
          'Basement Bath', 'Stairs to 2nd Floor'
        ];
        
        return (
          <div>
            {/* Trim Selections */}
            <div style={{ marginBottom: '3rem' }}>
              <h3 style={{ marginBottom: '1.5rem', color: '#333', borderBottom: '2px solid #007bff', paddingBottom: '0.5rem' }}>
                Trim & Molding Selections
              </h3>
              
              <TrimSelector
                label="1st Floor Casing"
                value={formData.casing1st}
                onChange={(v) => handleChange('casing1st', v)}
                disabled={!canEdit}
              />
              
              <TrimSelector
                label="1st Floor Base"
                value={formData.base1st}
                onChange={(v) => handleChange('base1st', v)}
                disabled={!canEdit}
              />
              
              <TrimSelector
                label="2nd Floor Casing"
                value={formData.casing2nd}
                onChange={(v) => handleChange('casing2nd', v)}
                disabled={!canEdit}
              />
              
              <TrimSelector
                label="2nd Floor Base"
                value={formData.base2nd}
                onChange={(v) => handleChange('base2nd', v)}
                disabled={!canEdit}
              />
              
              <FormField 
                label="Crown & Chair Details" 
                value={formData.trimDetails} 
                onChange={(v) => handleChange('trimDetails', v)} 
                disabled={!canEdit} 
                textarea 
              />
            </div>

            {/* Flooring Selections */}
            <div style={{ marginBottom: '3rem' }}>
              <h3 style={{ marginBottom: '1.5rem', color: '#333', borderBottom: '2px solid #007bff', paddingBottom: '0.5rem' }}>
                Flooring by Room
              </h3>
              
              {rooms.map(room => {
                const fieldName = `flooring${room.replace(/[^a-zA-Z0-9]/g, '')}`;
                const options = room.includes('Bath') || room === 'Laundry' 
                  ? ['Tile', 'LVP', 'Other'] 
                  : ['Tile', 'Carpet', 'LVP', 'Other'];
                
                return (
                  <div key={room}>
                    <SelectField 
                      label={room} 
                      value={formData[fieldName]} 
                      onChange={(v) => handleChange(fieldName, v)} 
                      disabled={!canEdit} 
                      options={options} 
                    />
                    {formData[fieldName] === 'Other' && (
                      <FormField 
                        label={`${room} - Other`}
                        value={formData[`${fieldName}Other`]} 
                        onChange={(v) => handleChange(`${fieldName}Other`, v)} 
                        disabled={!canEdit} 
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );

      case 'interior-tile':
        return (
          <div>
            <FormField label="Tile Floor Master Selection" value={formData.tileMaster} onChange={(v) => handleChange('tileMaster', v)} disabled={!canEdit} placeholder="From Comfort Flooring" />
            <FormField label="Grout Color (Master)" value={formData.groutMaster} onChange={(v) => handleChange('groutMaster', v)} disabled={!canEdit} />
            <FormField label="Tile Floor Bath #2" value={formData.tileBath2} onChange={(v) => handleChange('tileBath2', v)} disabled={!canEdit} placeholder="From Comfort Flooring" />
            <FormField label="Grout Color (Bath #2)" value={formData.groutBath2} onChange={(v) => handleChange('groutBath2', v)} disabled={!canEdit} />
            <FormField label="Tile Floor Bath #3" value={formData.tileBath3} onChange={(v) => handleChange('tileBath3', v)} disabled={!canEdit} placeholder="From Comfort Flooring" />
            <FormField label="Grout Color (Bath #3)" value={formData.groutBath3} onChange={(v) => handleChange('groutBath3', v)} disabled={!canEdit} />
            <FormField label="Tile Floor Bath #4" value={formData.tileBath4} onChange={(v) => handleChange('tileBath4', v)} disabled={!canEdit} placeholder="From Comfort Flooring" />
            <FormField label="Grout Color (Bath #4)" value={formData.groutBath4} onChange={(v) => handleChange('groutBath4', v)} disabled={!canEdit} />
            <FormField label="Master Shower Tile Wall" value={formData.showerWallTile} onChange={(v) => handleChange('showerWallTile', v)} disabled={!canEdit} textarea />
            <SelectField label="Size" value={formData.showerWallSize} onChange={(v) => handleChange('showerWallSize', v)} disabled={!canEdit} options={['3×6', '4×12', 'Wavey 4×12']} />
            <SelectField label="Grout Color" value={formData.showerWallGrout} onChange={(v) => handleChange('showerWallGrout', v)} disabled={!canEdit} options={['White', 'Black', 'Dark Grey']} />
            <FormField label="Master Shower Floor Tile Selection" value={formData.showerFloorTile} onChange={(v) => handleChange('showerFloorTile', v)} disabled={!canEdit} placeholder="From Comfort Flooring" />
            <SelectField label="Drain Cover Color" value={formData.drainCoverColor} onChange={(v) => handleChange('drainCoverColor', v)} disabled={!canEdit} options={['White', 'Black', 'Other']} />
            <SelectField label="Grout Color (Floor)" value={formData.showerFloorGrout} onChange={(v) => handleChange('showerFloorGrout', v)} disabled={!canEdit} options={['White', 'Black', 'Dark Grey', 'Other']} />
            <FormField label="Niche Tile Back" value={formData.nicheTile} onChange={(v) => handleChange('nicheTile', v)} disabled={!canEdit} />
            <FormField label="Niche Grout Color" value={formData.nicheGrout} onChange={(v) => handleChange('nicheGrout', v)} disabled={!canEdit} />
            <SelectField label="Schluter Strip Color" value={formData.schluterColor} onChange={(v) => handleChange('schluterColor', v)} disabled={!canEdit} options={['White', 'Black', 'Other']} />
          </div>
        );

      case 'interior-bathroom':
        return (
          <div>
            <FormField label="How Many Bathrooms?" type="number" value={formData.numBathrooms} onChange={(v) => handleChange('numBathrooms', v)} disabled={!canEdit} />
            
            {/* Generate bathroom sections based on numBathrooms */}
            {formData.numBathrooms && parseInt(formData.numBathrooms) > 0 && (
              <>
                {Array.from({ length: parseInt(formData.numBathrooms) }, (_, index) => {
                  const bathNum = index + 1;
                  const prefix = `bathroom${bathNum}`;
                  
                  return (
                    <div key={bathNum} style={{ 
                      marginTop: '2rem', 
                      padding: '1.5rem', 
                      backgroundColor: '#f8f9fa', 
                      borderRadius: '8px',
                      border: '2px solid #dee2e6'
                    }}>
                      <h3 style={{ 
                        marginTop: 0, 
                        marginBottom: '1.5rem', 
                        color: '#007bff',
                        borderBottom: '2px solid #007bff',
                        paddingBottom: '0.5rem'
                      }}>
                        Bathroom #{bathNum}
                      </h3>
                      
                      <SelectField 
                        label="Shower Base Type" 
                        value={formData[`${prefix}_showerBaseType`]} 
                        onChange={(v) => handleChange(`${prefix}_showerBaseType`, v)} 
                        disabled={!canEdit} 
                        options={['Mud Base', 'Schluter', 'Fiberglass', 'Fiberglass One Piece Shower', 'One Piece Tub', 'Tub with Tiled Surround']} 
                      />
                      
                      <FormField 
                        label="Shower/Tub Doors Location" 
                        value={formData[`${prefix}_doorLocation`]} 
                        onChange={(v) => handleChange(`${prefix}_doorLocation`, v)} 
                        disabled={!canEdit} 
                      />
                      
                      <FormField 
                        label="Size" 
                        value={formData[`${prefix}_bathSize`]} 
                        onChange={(v) => handleChange(`${prefix}_bathSize`, v)} 
                        disabled={!canEdit} 
                      />
                      
                      <FormField 
                        label="Color" 
                        value={formData[`${prefix}_bathColor`]} 
                        onChange={(v) => handleChange(`${prefix}_bathColor`, v)} 
                        disabled={!canEdit} 
                      />
                      
                      <FormField 
                        label="Glass" 
                        value={formData[`${prefix}_bathGlass`]} 
                        onChange={(v) => handleChange(`${prefix}_bathGlass`, v)} 
                        disabled={!canEdit} 
                      />
                      
                      <FormField 
                        label="Type" 
                        value={formData[`${prefix}_bathType`]} 
                        onChange={(v) => handleChange(`${prefix}_bathType`, v)} 
                        disabled={!canEdit} 
                      />
                      
                      <SelectField 
                        label="Stool Shape" 
                        value={formData[`${prefix}_stoolShape`]} 
                        onChange={(v) => handleChange(`${prefix}_stoolShape`, v)} 
                        disabled={!canEdit} 
                        options={['Round Stools', 'Elongated Stools', 'ADA Stools']} 
                      />
                      
                      <CheckboxField 
                        label="Zero Entry" 
                        checked={formData[`${prefix}_zeroEntry`]} 
                        onChange={(v) => handleChange(`${prefix}_zeroEntry`, v)} 
                        disabled={!canEdit} 
                      />
                      
                      {formData[`${prefix}_zeroEntry`] && (
                        <FormField 
                          label="Zero Entry Shower Details" 
                          value={formData[`${prefix}_zeroEntryDetails`]} 
                          onChange={(v) => handleChange(`${prefix}_zeroEntryDetails`, v)} 
                          disabled={!canEdit} 
                          textarea 
                        />
                      )}
                      
                      {formData[`${prefix}_showerBaseType`] === 'Mud Base' && (
                        <FormField 
                          label="Mud Base Shower Details" 
                          value={formData[`${prefix}_mudBaseDetails`]} 
                          onChange={(v) => handleChange(`${prefix}_mudBaseDetails`, v)} 
                          disabled={!canEdit} 
                          textarea 
                        />
                      )}
                      
                      {formData[`${prefix}_showerBaseType`]?.includes('Fiberglass') && (
                        <>
                          <CheckboxField 
                            label="Whirlpool Tub" 
                            checked={formData[`${prefix}_whirlpool`]} 
                            onChange={(v) => handleChange(`${prefix}_whirlpool`, v)} 
                            disabled={!canEdit} 
                          />
                          {formData[`${prefix}_whirlpool`] && (
                            <FormField 
                              label="Whirlpool Tub Details" 
                              value={formData[`${prefix}_whirlpoolDetails`]} 
                              onChange={(v) => handleChange(`${prefix}_whirlpoolDetails`, v)} 
                              disabled={!canEdit} 
                              textarea 
                            />
                          )}
                        </>
                      )}
                      
                      <CheckboxField 
                        label="Faucet" 
                        checked={formData[`${prefix}_bathFaucet`]} 
                        onChange={(v) => handleChange(`${prefix}_bathFaucet`, v)} 
                        disabled={!canEdit} 
                      />
                      
                      {formData[`${prefix}_bathFaucet`] && (
                        <FormField 
                          label="Faucet Details" 
                          value={formData[`${prefix}_faucetDetails`]} 
                          onChange={(v) => handleChange(`${prefix}_faucetDetails`, v)} 
                          disabled={!canEdit} 
                          placeholder="Bronze or Black" 
                        />
                      )}
                      
                      <div style={{ marginBottom: '2rem' }}>
                        <CabinetSelector
                          label={`Bathroom #${bathNum} Cabinets`}
                          canEdit={canEdit}
                          value={formData[`${prefix}_bathroomCabinet`]}
                          onChange={(v) => handleChange(`${prefix}_bathroomCabinet`, v)}
                        />
                      </div>
                      
                      {/* Mirror Options Selector */}
                      <div style={{ marginBottom: '2rem', padding: '1.5rem', backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #dee2e6' }}>
                        <div 
                          onClick={() => {
                            const key = `showMirrorOptions${bathNum}`;
                            setShowMirrorOptions(prev => ({ ...prev, [key]: !prev[key] }));
                          }}
                          style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'space-between',
                            cursor: 'pointer',
                            userSelect: 'none'
                          }}
                        >
                          <div>
                            <h4 style={{ margin: '0 0 0.25rem 0', fontSize: '1rem', fontWeight: '600', color: '#333' }}>
                              Mirror Options
                              {formData[`${prefix}_mirrorOptions`] && (
                                <span style={{ color: '#007bff', fontSize: '0.9rem', fontWeight: 'normal', marginLeft: '0.5rem' }}>
                                  ({formData[`${prefix}_mirrorOptions`]})
                                </span>
                              )}
                            </h4>
                            <p style={{ fontSize: '0.9rem', color: '#666', margin: 0 }}>
                              Choose a mirror style for this bathroom
                            </p>
                          </div>
                          <span style={{ fontSize: '1.5rem', color: '#666' }}>
                            {showMirrorOptions[`showMirrorOptions${bathNum}`] ? '⌄' : '^'}
                          </span>
                        </div>
                        
                        {showMirrorOptions[`showMirrorOptions${bathNum}`] && (
                          <>
                            <div
                              style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                                gap: '1rem',
                                marginTop: '1rem'
                              }}
                            >
                              {[
                                { name: 'Black', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/mirror/delta-black.jpg' },
                                { name: 'Grey', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/mirror/delta-grey.jpg' },
                                { name: 'Silver', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/mirror/delta-silver.jpg' },
                                { name: 'White', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/mirror/delta-white.jpg' },
                              ].map((option) => (
                                <div
                                  key={option.name}
                                  onClick={() => {
                                    if (canEdit) {
                                      handleChange(`${prefix}_mirrorOptions`, option.name);
                                      const key = `showMirrorOptions${bathNum}`;
                                      setShowMirrorOptions(prev => ({ ...prev, [key]: false }));
                                    }
                                  }}
                                  style={{
                                    cursor: canEdit ? 'pointer' : 'not-allowed',
                                    textAlign: 'center',
                                    opacity: !canEdit ? 0.6 : 1,
                                  }}
                                >
                                  <div
                                    style={{
                                      width: '100%',
                                      height: '120px',
                                      borderRadius: '8px',
                                      border: formData[`${prefix}_mirrorOptions`] === option.name ? '3px solid #007bff' : '2px solid #ddd',
                                      boxShadow: formData[`${prefix}_mirrorOptions`] === option.name ? '0 4px 12px rgba(0,123,255,0.3)' : '0 2px 4px rgba(0,0,0,0.1)',
                                      transition: 'all 0.2s',
                                      transform: formData[`${prefix}_mirrorOptions`] === option.name ? 'scale(1.05)' : 'scale(1)',
                                      overflow: 'hidden',
                                      position: 'relative',
                                    }}
                                  >
                                    <img
                                      src={option.image}
                                      alt={option.name}
                                      style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'contain',
                                      }}
                                    />
                                    {formData[`${prefix}_mirrorOptions`] === option.name && (
                                      <div
                                        style={{
                                          position: 'absolute',
                                          top: '5px',
                                          right: '5px',
                                          backgroundColor: '#007bff',
                                          color: 'white',
                                          borderRadius: '50%',
                                          width: '24px',
                                          height: '24px',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          fontSize: '14px',
                                          fontWeight: 'bold',
                                        }}
                                      >
                                        ✓
                                      </div>
                                    )}
                                  </div>
                                  <p
                                    style={{
                                      margin: '0.5rem 0 0 0',
                                      fontSize: '0.85rem',
                                      color: formData[`${prefix}_mirrorOptions`] === option.name ? '#007bff' : '#333',
                                      fontWeight: formData[`${prefix}_mirrorOptions`] === option.name ? '600' : '400',
                                    }}
                                  >
                                    {option.name}
                                  </p>
                                </div>
                              ))}
                            </div>

                            {!canEdit && (
                              <p style={{ marginTop: '1rem', fontSize: '0.85rem', color: '#dc3545', fontStyle: 'italic' }}>
                                Mirror selection is locked. Contact administrator to make changes.
                              </p>
                            )}
                          </>
                        )}
                      </div>
                      
                      <FormField 
                        label="Additional Details" 
                        value={formData[`${prefix}_bathroomAdditional`]} 
                        onChange={(v) => handleChange(`${prefix}_bathroomAdditional`, v)} 
                        disabled={!canEdit} 
                        textarea 
                      />
                    </div>
                  );
                })}
              </>
            )}
          </div>
        );
    
      case 'interior-kitchen':
        return (
            <div>
            {/* Kitchen Sink Selector */}
            <div style={{ marginBottom: '2rem' }}>
                <SinkSelector
                canEdit={canEdit}
                value={formData.kitchenSink}
                onChange={(v) => handleChange('kitchenSink', v)}
                />
            </div>

            {/* Kitchen Faucet Selector */}
            <div style={{ marginBottom: '2rem' }}>
                <KitchenFaucetSelector
                canEdit={canEdit}
                value={formData.kitchenFaucet}
                onChange={(v) => handleChange('kitchenFaucet', v)}
                />
            </div>

            {/* Additional Kitchen Fields */}
            <SelectField 
                label="Kitchen Countertop Material" 
                value={formData.countertopMaterial} 
                onChange={(v) => handleChange('countertopMaterial', v)} 
                disabled={!canEdit} 
                options={['Laminate', 'Marble', 'Quartz', 'Granite']} 
            />
            
            <FormField 
                label="Countertop Color Selection" 
                value={formData.countertopColor} 
                onChange={(v) => handleChange('countertopColor', v)} 
                disabled={!canEdit} 
            />
            
            <CheckboxField 
                label='4" Backsplash' 
                checked={formData.backsplash4inch} 
                onChange={(v) => handleChange('backsplash4inch', v)} 
                disabled={!canEdit} 
            />

            <SelectField 
                label="Kitchen Backsplash Size" 
                value={formData.backsplashSize} 
                onChange={(v) => handleChange('backsplashSize', v)} 
                disabled={!canEdit} 
                options={['3×6', '4×12', 'Wavey 4×12', 'Other']} 
            />
            
            <SelectField 
                label="Kitchen Schluter Strip Color" 
                value={formData.kitchenSchluterColor} 
                onChange={(v) => handleChange('kitchenSchluterColor', v)} 
                disabled={!canEdit} 
                options={['White', 'Black', 'Other']} 
            />
            
            <SelectField 
                label="Kitchen Grout Color" 
                value={formData.kitchenGroutColor} 
                onChange={(v) => handleChange('kitchenGroutColor', v)} 
                disabled={!canEdit} 
                options={['White', 'Black', 'Other']} 
            />

            <CheckboxField 
                label="Homeowner to Supply Cabinets" 
                checked={formData.homeownerSupply} 
                onChange={(v) => handleChange('homeownerSupply', v)} 
                disabled={!canEdit} 
            />

            <FormField 
                label="Additional Kitchen Details" 
                value={formData.kitchenAdditional} 
                onChange={(v) => handleChange('kitchenAdditional', v)} 
                disabled={!canEdit} 
                textarea 
            />
            </div>
        );

      case 'interior-cabinets':
        return (
          <div>
            <FormField label="Door Style" value={formData.cabinetDoorStyle} onChange={(v) => handleChange('cabinetDoorStyle', v)} disabled={!canEdit} />
            <FormField label="Door Pull Style" value={formData.doorPullStyle} onChange={(v) => handleChange('doorPullStyle', v)} disabled={!canEdit} />
            <FormField label="Pull Color" value={formData.pullColor} onChange={(v) => handleChange('pullColor', v)} disabled={!canEdit} />
            <CheckboxField label="Island to Match" checked={formData.islandMatch} onChange={(v) => handleChange('islandMatch', v)} disabled={!canEdit} />
            {!formData.islandMatch && (
              <>
                <FormField label="Island Door Pull Style" value={formData.islandPullStyle} onChange={(v) => handleChange('islandPullStyle', v)} disabled={!canEdit} />
                <FormField label="Island Pull Color" value={formData.islandPullColor} onChange={(v) => handleChange('islandPullColor', v)} disabled={!canEdit} />
              </>
            )}
            <CheckboxField label="Homeowner to Supply" checked={formData.homeownerSupply} onChange={(v) => handleChange('homeownerSupply', v)} disabled={!canEdit} />
            <CheckboxField label="Microwave in Island" checked={formData.microwaveIsland} onChange={(v) => handleChange('microwaveIsland', v)} disabled={!canEdit} />
            <CheckboxField label="Trash in Island" checked={formData.trashIsland} onChange={(v) => handleChange('trashIsland', v)} disabled={!canEdit} />
            {/* Kitchen Cabinet Selector */}
            <div style={{ marginBottom: '2rem' }}>
                <CabinetSelector
                label={'Kitchen Cabinets'}
                canEdit={canEdit}
                value={formData.kitchenCabinet}
                onChange={(v) => handleChange('kitchenCabinet', v)}
                />
            </div>

            {/* Island Cabinet Selector */}
            <div style={{ marginBottom: '2rem' }}>
                <CabinetSelector
                label={'Island Cabinets'}
                canEdit={canEdit}
                value={formData.islandCabinet}
                onChange={(v) => handleChange('islandCabinet', v)}
                />
            </div>

            {/* Upper Cabinet Selector */}
            <div style={{ marginBottom: '2rem' }}>
                <CabinetSelector
                label={'Upper Cabinets'}
                canEdit={canEdit}
                value={formData.upperCabinetColor}
                onChange={(v) => handleChange('upperCabinetColor', v)}
                />
            </div>

            {/* Back of Island Cabinet Selector */}
            <div style={{ marginBottom: '2rem' }}>
                <CabinetSelector
                label={'Back of Island Color'}
                canEdit={canEdit}
                value={formData.islandBackColor}
                onChange={(v) => handleChange('islandBackColor', v)}
                />
            </div>
            {/* <SelectField label="Island Back Options" value={formData.islandBackOptions} onChange={(v) => handleChange('islandBackOptions', v)} disabled={!canEdit} options={['Plain', 'Shiplap', 'Fluted']} /> */}
            <CheckboxField label="Laundry Countertops and Vanities to Match Kitchen" checked={formData.laundryMatch} onChange={(v) => handleChange('laundryMatch', v)} disabled={!canEdit} />
            <FormField label="Supplier" value={formData.cabinetSupplier} onChange={(v) => handleChange('cabinetSupplier', v)} disabled={!canEdit} />
          </div>
        );

      case 'interior-walls':
        return (
          <div>
            <PaintColorSelector
                label="Ceiling Color"
                value={formData.ceilingColor}
                onChange={(v) => handleChange('ceilingColor', v)}
                disabled={!canEdit}
                required={false}
            />
            <PaintColorSelector
                label="Wall Paint Color"
                value={formData.wallPaintColor}
                onChange={(v) => handleChange('wallPaintColor', v)}
                disabled={!canEdit}
                required={false}
            />
            <CheckboxField label="All Walls Same Color?" checked={formData.allWallsSame} onChange={(v) => handleChange('allWallsSame', v)} disabled={!canEdit} />
            {!formData.allWallsSame && (
              <>
                <SelectField 
                    label="Split By" 
                    value={formData.wallSplitBy} 
                    onChange={(v) => handleChange('wallSplitBy', v)} 
                    disabled={!canEdit} 
                    options={['Room', 'Accent Wall']} 
                />
                <FormField 
                    label="Pick Rooms and Colors" 
                    value={formData.roomColors} 
                    onChange={(v) => handleChange('roomColors', v)} 
                    disabled={!canEdit} 
                    textarea 
                    placeholder="Describe which rooms have different colors" 
                />
                <PaintColorSelector
                    label="Accent Color"
                    value={formData.accentColor}
                    onChange={(v) => handleChange('accentColor', v)}
                    disabled={!canEdit}
                    required={false}
                />
              </>
            )}
            {/* Ceiling Fan Options Selector */}
            <div style={{ marginBottom: '2rem', padding: '1.5rem', backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #dee2e6' }}>
              <div 
                onClick={() => setShowCeilingFanOptions(!showCeilingFanOptions)}
                style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  userSelect: 'none'
                }}
              >
                <div>
                  <h4 style={{ margin: '0 0 0.25rem 0', fontSize: '1rem', fontWeight: '600', color: '#333' }}>
                    Ceiling Fan Options
                    {formData.ceilingFanOptions && (
                      <span style={{ color: '#007bff', fontSize: '0.9rem', fontWeight: 'normal', marginLeft: '0.5rem' }}>
                        ({formData.ceilingFanOptions})
                      </span>
                    )}
                  </h4>
                  <p style={{ fontSize: '0.9rem', color: '#666', margin: 0 }}>
                    Choose a ceiling fan style for your home
                  </p>
                </div>
                <span style={{ fontSize: '1.5rem', color: '#666' }}>
                  {showCeilingFanOptions ? '⌄' : '^'}
                </span>
              </div>
              
              {showCeilingFanOptions && (
                <>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                      gap: '1rem',
                      marginTop: '1rem'
                    }}
                  >
                    {[
                      { name: 'Dark', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/fan/ceiling-fan-brown.jpg' },
                      { name: 'Light', image: 'https://cdn.jsdelivr.net/gh/foreverkatie123/selection-images/fan/ceiling-fan-wood.jpg' },
                    ].map((option) => (
                      <div
                        key={option.name}
                        onClick={() => {
                          if (canEdit) {
                            handleChange('ceilingFanOptions', option.name);
                            setShowCeilingFanOptions(false);
                          }
                        }}
                        style={{
                          cursor: canEdit ? 'pointer' : 'not-allowed',
                          textAlign: 'center',
                          opacity: !canEdit ? 0.6 : 1,
                        }}
                      >
                        <div
                          style={{
                            width: '100%',
                            height: '150px',
                            borderRadius: '8px',
                            border: formData.ceilingFanOptions === option.name ? '3px solid #007bff' : '2px solid #ddd',
                            boxShadow: formData.ceilingFanOptions === option.name ? '0 4px 12px rgba(0,123,255,0.3)' : '0 2px 4px rgba(0,0,0,0.1)',
                            transition: 'all 0.2s',
                            transform: formData.ceilingFanOptions === option.name ? 'scale(1.05)' : 'scale(1)',
                            overflow: 'hidden',
                            position: 'relative',
                          }}
                        >
                          <img
                            src={option.image}
                            alt={option.name}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            }}
                          />
                          {formData.ceilingFanOptions === option.name && (
                            <div
                              style={{
                                position: 'absolute',
                                top: '5px',
                                right: '5px',
                                backgroundColor: '#007bff',
                                color: 'white',
                                borderRadius: '50%',
                                width: '24px',
                                height: '24px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '14px',
                                fontWeight: 'bold',
                              }}
                            >
                              ✓
                            </div>
                          )}
                        </div>
                        <p
                          style={{
                            margin: '0.5rem 0 0 0',
                            fontSize: '0.85rem',
                            color: formData.ceilingFanOptions === option.name ? '#007bff' : '#333',
                            fontWeight: formData.ceilingFanOptions === option.name ? '600' : '400',
                          }}
                        >
                          {option.name}
                        </p>
                      </div>
                    ))}
                  </div>

                  {!canEdit && (
                    <p style={{ marginTop: '1rem', fontSize: '0.85rem', color: '#dc3545', fontStyle: 'italic' }}>
                      Ceiling Fan selection is locked. Contact administrator to make changes.
                    </p>
                  )}
                </>
              )}
            </div>
            <FormField label="Custom Painting" value={formData.customPainting} onChange={(v) => handleChange('customPainting', v)} disabled={!canEdit} textarea />
          </div>
        );

      default:
        return <div>Section content not found</div>;
    }
  };

  // Load existing form data on mount
  useEffect(() => {
    const loadFormData = async () => {
      if (!db || !currentProject?.id) return;
      
      try {
        const { collection, doc, getDoc } = await import('firebase/firestore');
        const selectionsRef = doc(db, 'projects', currentProject.id, 'selections', 'formData');
        const selectionsDoc = await getDoc(selectionsRef);
        
        if (selectionsDoc.exists()) {
          setFormData(selectionsDoc.data());
          setLastSaved(new Date());
        }
      } catch (error) {
        console.error('Error loading form data:', error);
      }
    };
    
    loadFormData();
  }, [db, currentProject?.id]);

  // Auto-save function
  const saveFormData = async (data) => {
    if (!db || !currentProject?.id) return;
    
    setIsSaving(true);
    try {
      const { collection, doc, setDoc } = await import('firebase/firestore');
      const selectionsRef = doc(db, 'projects', currentProject.id, 'selections', 'formData');
      
      await setDoc(selectionsRef, {
        ...data,
        lastModified: new Date(),
        modifiedBy: user?.uid || 'unknown'
      }, { merge: true });
      
      setLastSaved(new Date());
      setSaveMessage('Saved successfully');
      setTimeout(() => setSaveMessage(''), 2000);
    } catch (error) {
      console.error('Error saving form data:', error);
      setSaveMessage('Save failed');
      setTimeout(() => setSaveMessage(''), 2000);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <PageHeader
        currentPage="selections"
        onNavigate={onNavigate}
        currentProject={currentProject}
        projects={projects}
        onProjectChange={onProjectChange}
        userRole={userRole}
        showPrint={true}
        onPrint={() => openPrintPreview(formData, currentProject)}
      />

      <div style={{ 
        minHeight: '100vh',
        backgroundColor: '#f8f9fa',
        padding: '2rem'
      }}>
        <div style={{ 
          minHeight: '100vh',
          backgroundColor: '#f8f9fa',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          width: '100%'
        }}>
          {/* Progress Bar */}
          <div style={{ marginBottom: '2rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
              <span style={{ fontSize: '14px', color: '#666' }}>Section {currentSection + 1} of {sections.length}</span>
              <span style={{ fontSize: '14px', color: '#666' }}>{Math.round(((currentSection + 1) / sections.length) * 100)}% Complete</span>
            </div>
            <div style={{
              width: '100%',
              height: '8px',
              backgroundColor: '#e0e0e0',
              borderRadius: '4px',
              overflow: 'hidden'
            }}>
              <div style={{
                width: `${((currentSection + 1) / sections.length) * 100}%`,
                height: '100%',
                backgroundColor: '#007bff',
                transition: 'width 0.3s ease'
              }}></div>
            </div>
          </div>

          {/* Section Tabs */}
          <div style={{
            display: 'flex',
            gap: '0.5rem',
            marginBottom: '1.5rem',
            overflowX: 'auto',
            paddingBottom: '0.5rem'
          }}>
            {sections.map((section, index) => (
              <button
                key={section.id}
                onClick={() => { setCurrentSection(index); window.scrollTo(0, 0); }}
                style={{
                  padding: '0.75rem 1rem',
                  border: currentSection === index ? '2px solid #007bff' : '1px solid #ddd',
                  borderRadius: '8px',
                  backgroundColor: currentSection === index ? '#e3f2fd' : '#fff',
                  color: currentSection === index ? '#007bff' : '#666',
                  fontWeight: currentSection === index ? '600' : '400',
                  fontSize: '14px',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                <span>{section.icon}</span>
                <span style={{ display: window.innerWidth > 768 ? 'inline' : 'none' }}>
                  {section.title.replace(/^[^\s]+ /, '')}
                </span>
              </button>
            ))}
          </div>

          {/* Content */}
          <div style={{
            backgroundColor: '#fff',
            padding: '2rem',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            marginBottom: '1.5rem',
            minHeight: '400px'
          }}>
            <h2 style={{ 
              marginTop: 0, 
              marginBottom: '1.5rem',
              borderBottom: '2px solid #007bff',
              paddingBottom: '0.75rem'
            }}>
              {sections[currentSection].title}
            </h2>
            
            {renderSection()}
          </div>

          {/* Navigation Buttons */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: '1rem'
          }}>
            <button
              onClick={prevSection}
              disabled={currentSection === 0 || isSaving}
              style={{
                padding: '12px 24px',
                backgroundColor: currentSection === 0 ? '#e0e0e0' : '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '16px',
                cursor: currentSection === 0 || isSaving ? 'not-allowed' : 'pointer',
                opacity: currentSection === 0 || isSaving ? 0.6 : 1
              }}
            >
              ← Previous
            </button>

          {currentSection === sections.length - 1 ? (
            <button
              onClick={handleSubmit}
              disabled={isSaving}
              style={{
                padding: '12px 48px',
                backgroundColor: isSaving ? '#e0e0e0' : '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: isSaving ? 'not-allowed' : 'pointer',
                opacity: isSaving ? 0.6 :1
              }}
            >
              ✓ Submit Selections
            </button>
          ) : (
             <button
                onClick={nextSection}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '16px',
                  cursor: 'pointer'
                }}
              >
                Next →
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default SelectionsForm;